import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { apiFetch, abrirArchivoProtegido } from "../utils/api";
import { EncabezadoDetalle } from "../components/TemaDetalle";
import { abrirArchivoSeguro } from "../utils/upload";
import MiniaturaArchivo from "../components/MiniaturaArchivo";
import NotaEmergente from "../components/NotaEmergente";
import { parametro, useParametros } from "../utils/parametros";
import {
  ESTADOS_SOLICITUD,
  Badge,
  BadgeFormaPago,
  estadoVisible,
  estadoItemVisible,
  itemEntregado,
  formatoMonto,
  sumarPorMoneda,
  textoPorMoneda,
  mapaEstadosSolicitud,
  mapaEstadosItem,
} from "../utils/estados";
import BarraProgreso from "../components/BarraProgreso";
import { Ayuda } from "../context/AyudaContext";
import ConfirmarNoActivoModal from "../components/ConfirmarNoActivoModal";
import PrepararPagoModal from "../components/PrepararPagoModal";
import { nombreSinCantidad } from "../utils/nombreProducto";
import BuscadorPersona from "../components/BuscadorPersona";
import Cargando from "../components/Cargando";

// Cada producto admite como máximo 3 cotizaciones (el backend también lo
// valida, esto solo evita que compras llegue a chocar con el error).

// Texto del aviso de retiro según el día/hora elegidos (cualquiera de los dos
// puede faltar). Ej.: "Puede venir a retirar el producto el viernes, 25 de
// septiembre a las 9:00 a. m. en la oficina de Compras."
function mensajeRetiroPara(fecha, hora) {
  let cuando = "";
  if (fecha) {
    const dia = new Date(`${fecha}T00:00:00`).toLocaleDateString("es-NI", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
    cuando += ` el ${dia}`;
  }
  if (hora) {
    const h = new Date(`2000-01-01T${hora}:00`).toLocaleTimeString("es-NI", {
      hour: "numeric",
      minute: "2-digit",
    });
    cuando += ` a las ${h}`;
  }
  return `Puede venir a retirar el producto${cuando} en la oficina de Compras.`;
}

// Proveedor al que se le compró cada producto (el de la cotización aprobada) — las ventanas de
// notificar y de entregar muestran los productos agrupados por proveedor, porque no todos
// llegan al mismo tiempo.
// Si el producto se compró en partes a varios proveedores, el grupo se llama
// con todos y cuánto compró cada uno, ej. "Edatel (2) + Mastertec (1)" (antes
// quedaba todo bajo el primero).
const proveedorDeItem = (it) => {
  const varios = it.proveedores_elegidos || [];
  if (varios.length > 1) {
    return varios.map((p) => (p.cantidad ? `${p.proveedor_nombre} (${p.cantidad})` : p.proveedor_nombre)).join(" + ");
  }
  return it.cotizacion_elegida?.proveedor_nombre || "Sin proveedor asignado";
};
// Cuántos proveedores distintos tiene la solicitud (un grupo "Edatel + Mastertec" cuenta como dos).
const contarProveedores = (items) => {
  const nombres = new Set();
  items.forEach((it) => {
    const varios = it.proveedores_elegidos || [];
    if (varios.length > 0) varios.forEach((p) => nombres.add(p.proveedor_nombre));
    else if (it.cotizacion_elegida?.proveedor_nombre) nombres.add(it.cotizacion_elegida.proveedor_nombre);
  });
  return nombres.size;
};
const agruparPorProveedor = (lista) => {
  const grupos = new Map();
  lista.forEach((it) => {
    const nombre = proveedorDeItem(it);
    if (!grupos.has(nombre)) grupos.set(nombre, []);
    grupos.get(nombre).push(it);
  });
  return [...grupos.entries()];
};

// Una vez que gerencia aprobó parte (o toda) la cantidad pedida de un
// ítem, lo que importa mostrar de ahí en adelante (pagos, avisar retiro,
// entrega) es esa cantidad aprobada, no la cantidad original pedida — si
// no, esas pantallas muestran una cantidad que ya no es la que se va a
// comprar/entregar. Antes de que haya alguna aprobación, se sigue mostrando
// la cantidad pedida como siempre.
const cantidadRelevante = (item) =>
  item.cantidad_aprobada > 0 ? item.cantidad_aprobada : item.descartado ? 0 : item.cantidad;

const unidadActivoVacia = (numero) => ({
  numero_unidad: numero,
  codigo_activo: "",
  marca: "",
  modelo: "",
  numero_serie: "",
  fecha_adquisicion: "",
  edificio_id: "",
});

// Un activo se registra por UNIDAD física, no por línea de compra — si
// pidieron 2 computadoras, son 2 formularios con su propio código/serie.
const formActivoVacio = (cantidad) => ({
  es_activo_fijo: false,
  unidades: Array.from({ length: cantidad }, (_, i) => unidadActivoVacia(i + 1)),
});

const custodioVacio = { nombre: "", correo: "", area_id: "", cargo: "", edificio_id: "" };

function DetalleSolicitud() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Parámetros del sistema (máximo de cotizaciones por producto…): la pantalla se repinta cuando llegan.
  useParametros();

  const usuario = JSON.parse(localStorage.getItem("usuario") || "null");
  const esAdmin = usuario?.rol === "administrador";
  const esCompras = usuario?.rol === "compras" || esAdmin;
  const esAprobador = usuario?.rol === "aprobador" || esAdmin;
  // Cualquiera que gestiona el proceso (compras, aprobador o quien paga), no el solicitante externo
  const esStaff = esCompras || esAprobador || usuario?.rol === "pagos";

  const [solicitud, setSolicitud] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [areas, setAreas] = useState([]);
  const [edificios, setEdificios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [procesando, setProcesando] = useState(false);

  // Ventana "¿estos artículos no son activos?" antes de avisar el retiro —
  // guarda los nombres a confirmar; null = cerrada.
  const [confirmandoNoActivos, setConfirmandoNoActivos] = useState(null);
  const [formsActivo, setFormsActivo] = useState({});
  // Al tildar "Es activo fijo" se pregunta si se llenan los datos ahí mismo
  // o después (desde la pantalla dedicada) — { [itemId]: true } mientras se
  // está mostrando el formulario completo ahí mismo.
  const [llenandoActivoAhora, setLlenandoActivoAhora] = useState({});

  const [editandoItemId, setEditandoItemId] = useState(null);
  const [formEditarItem, setFormEditarItem] = useState({
    descripcion_producto: "",
    descripcion_detallada: "",
    cantidad: "",
    unidad: "",
  });
  const [guardandoItem, setGuardandoItem] = useState(false);
  const descripcionEditarItemRef = useRef(null);
  const descripcionDetalladaEditarItemRef = useRef(null);

  // Igual que en "Nueva Solicitud": estos campos crecen con el texto en vez
  // de desbordarse hacia los lados o hacia abajo.
  useEffect(() => {
    const el = descripcionEditarItemRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [formEditarItem.descripcion_producto]);

  useEffect(() => {
    const el = descripcionDetalladaEditarItemRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [formEditarItem.descripcion_detallada]);

  const [itemsAbiertos, setItemsAbiertos] = useState({});
  // La lista de productos solicitados arranca abierta; se puede ocultar con el botón de la sección.
  const [mostrarItems, setMostrarItems] = useState(true);
  // Proveedor cuya tarjeta se tocó en "Cotizaciones de proveedores": debajo se listan sus productos.
  const [proveedorVisto, setProveedorVisto] = useState(null);
  // Sección abierta en una solicitud ya recibida: null | "items" | "productos" | "activos".
  const [panelFinal, setPanelFinal] = useState(null);
  // La descripción detallada del producto queda oculta por defecto (se ve
  // solo el nombre del producto) — "Ver más detalles" la despliega.
  const [detalleProductoAbierto, setDetalleProductoAbierto] = useState({});
  const [historialAbierto, setHistorialAbierto] = useState(false);
  const [mostrarActaBienes, setMostrarActaBienes] = useState(false);
  const [menuAccionesAbierto, setMenuAccionesAbierto] = useState(false);
  const [menuActasAbierto, setMenuActasAbierto] = useState(false);
  const [menuCotizacionAbierto, setMenuCotizacionAbierto] = useState(null);
  const [menuCotizacionPos, setMenuCotizacionPos] = useState(null);
  const [custodioId, setCustodioId] = useState("");
  const [nuevoCustodio, setNuevoCustodio] = useState(custodioVacio);
  const [creandoCustodio, setCreandoCustodio] = useState(false);

  const [modalRetiroAbierto, setModalRetiroAbierto] = useState(false);
  const [cantidadesAvisar, setCantidadesAvisar] = useState({});
  // Con varios proveedores la ventana de notificar primero muestra los proveedores; este es el que se abrió.
  const [proveedorAvisar, setProveedorAvisar] = useState(null);
  // Ventana "Preparar pago": documentos que Compras debe tener listos antes de que el pago llegue a Pagos.
  const [prepararPago, setPrepararPago] = useState(null);
  const [guardandoDocs, setGuardandoDocs] = useState(false);
  const [errorDocs, setErrorDocs] = useState("");
  const [unidadesAvisarSeleccionadas, setUnidadesAvisarSeleccionadas] = useState([]);
  const [mensajeRetiro, setMensajeRetiro] = useState("");
  const [fechaRetiro, setFechaRetiro] = useState("");
  const [horaRetiro, setHoraRetiro] = useState("");
  const [enviandoRetiro, setEnviandoRetiro] = useState(false);
  const [busquedaAvisar, setBusquedaAvisar] = useState("");
  const [busquedaActivosEntregados, setBusquedaActivosEntregados] = useState("");
  // Al volver de llenar los datos de un activo (pantalla /activo/:itemId) se
  // reabre sola la ventana de "Avisar retiro", sin repetir "Más acciones".
  const location = useLocation();
  const reabrirAvisarHecho = useRef(false);

  const cargar = () => {
    setCargando(true);
    Promise.all([
      apiFetch(`/api/solicitudes/${id}`),
      esCompras ? apiFetch("/api/usuarios") : Promise.resolve([]),
      esCompras ? apiFetch("/api/areas") : Promise.resolve([]),
      esCompras ? apiFetch("/api/edificios") : Promise.resolve([]),
    ])
      .then(([solicitudData, usuariosData, areasData, edificiosData]) => {
        setSolicitud(solicitudData);
        setUsuarios(usuariosData || []);
        setAreas(areasData || []);
        setEdificios(edificiosData || []);

        const iniciales = {};
        (solicitudData.items || []).forEach((it) => {
          const existentes = it.activos || [];
          const unidades = Array.from({ length: cantidadRelevante(it) }, (_, i) => {
            const numero = i + 1;
            const existente = existentes.find((a) => a.numero_unidad === numero);
            return existente
              ? {
                  numero_unidad: numero,
                  codigo_activo: existente.codigo_activo || "",
                  marca: existente.marca || "",
                  modelo: existente.modelo || "",
                  numero_serie: existente.numero_serie || "",
                  fecha_adquisicion: existente.fecha_adquisicion
                    ? existente.fecha_adquisicion.slice(0, 10)
                    : "",
                  edificio_id: existente.edificio_id || "",
                }
              : unidadActivoVacia(numero);
          });
          iniciales[it.id] = { es_activo_fijo: Boolean(it.es_activo_fijo), unidades };
        });
        setFormsActivo(iniciales);
      })
      .catch((err) => setError(err.message))
      .finally(() => setCargando(false));
  };

  useEffect(cargar, [id]);

  useEffect(() => {
    if (!location.state?.reabrirAvisar || !solicitud || !esCompras || reabrirAvisarHecho.current) return;
    reabrirAvisarHecho.current = true;
    navigate(location.pathname, { replace: true, state: null });
    abrirModalRetiro();
    // Se recuperan el día, la hora y el mensaje que ya se habían escrito antes de ir a llenar los activos.
    const avisoGuardado = location.state?.avisoRetiro;
    if (avisoGuardado) {
      setFechaRetiro(avisoGuardado.fecha || "");
      setHoraRetiro(avisoGuardado.hora || "");
      setMensajeRetiro(avisoGuardado.mensaje || mensajeRetiroPara(avisoGuardado.fecha || "", avisoGuardado.hora || ""));
    }
    // Si se venía de llenar datos de un activo, se vuelve directo a la lista de ESE proveedor.
    const proveedorDeVuelta = location.state?.proveedorAvisar;
    if (proveedorDeVuelta) {
      marcarPendientesDeProveedor(proveedorDeVuelta);
      setProveedorAvisar(proveedorDeVuelta);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, solicitud, esCompras]);

  // Por defecto, el responsable del bien es el mismo solicitante — ya tenemos
  // sus datos. Solo se cambia si compras elige a otra persona a propósito.
  useEffect(() => {
    if (solicitud && !custodioId) {
      setCustodioId(String(solicitud.solicitante_id));
    }
  }, [solicitud, custodioId]);

  const cambiarEstado = async (estado) => {
    setProcesando(true);
    setError("");
    try {
      await apiFetch(`/api/solicitudes/${id}/estado`, {
        method: "POST",
        body: JSON.stringify({ estado }),
      });
      cargar();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcesando(false);
    }
  };

  const marcarCotizando = async () => {
    setProcesando(true);
    setError("");
    try {
      await apiFetch(`/api/solicitudes/${id}/marcar-cotizando`, { method: "POST", body: JSON.stringify({}) });
      cargar();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcesando(false);
    }
  };

  const reactivarSolicitud = async () => {
    if (!window.confirm("¿Reactivar esta solicitud? Vuelve a cotizando para buscar nuevas cotizaciones.")) {
      return;
    }
    setProcesando(true);
    setError("");
    try {
      await apiFetch(`/api/solicitudes/${id}/reactivar`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      cargar();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcesando(false);
    }
  };

  // Un ítem de activo fijo no se puede avisar como listo mientras falte el
  // código de alguna de sus unidades (el backend también lo valida).
  const activoIncompleto = (it) =>
    it.es_activo_fijo &&
    ((it.activos || []).length < cantidadRelevante(it) || (it.activos || []).some((a) => !a.codigo_activo));

  // Si compras marcó el checkbox "Es activo fijo" de un ítem pero nunca le
  // dio "💾 Guardar datos de activo", ese cambio solo existe en pantalla —
  // en la base de datos el ítem sigue siendo un producto normal, así que
  // se colaría en "Avisar retiro" como si nunca hubiera sido un activo.
  const itemsActivoSinGuardar = () =>
    (solicitud.items || []).filter(
      (it) => !it.es_activo_fijo && formsActivo[it.id]?.es_activo_fijo
    );

  // Marca lo pendiente de notificar de UN proveedor (productos normales y unidades de activo ya listas).
  const marcarPendientesDeProveedor = (proveedor) => {
    const cantidades = {};
    const unidades = [];
    (solicitud.items || [])
      .filter((it) => proveedorDeItem(it) === proveedor && it.puede_notificar)
      .forEach((it) => {
        if (it.es_activo_fijo) {
          (it.activos || []).forEach((a) => {
            if (!a.avisado && a.codigo_activo) unidades.push(a.id);
          });
        } else {
          const pendiente = cantidadRelevante(it) - it.cantidad_lista;
          const rechazado = it.descartado && !(Number(it.cantidad_aprobada) > 0);
          if (!rechazado && pendiente > 0) cantidades[it.id] = pendiente;
        }
      });
    setCantidadesAvisar(cantidades);
    setUnidadesAvisarSeleccionadas(unidades);
  };

  const abrirModalRetiro = () => {
    const pendientesSinGuardar = itemsActivoSinGuardar();
    if (pendientesSinGuardar.length > 0) {
      setError(
        `Marcaste "${pendientesSinGuardar
          .map((it) => it.descripcion_producto)
          .join(", ")}" como activo fijo pero todavía no guardaste sus datos — usa "💾 Guardar datos de activo" en ese ítem antes de notificar el retiro.`
      );
      return;
    }
    const cantidades = {};
    (solicitud.items || []).forEach((it) => {
      if (it.es_activo_fijo || !it.puede_notificar) return;
      const pendiente = cantidadRelevante(it) - it.cantidad_lista;
      if (pendiente > 0) cantidades[it.id] = pendiente;
    });
    setCantidadesAvisar(cantidades);

    const unidades = (solicitud.items || [])
      .filter((it) => it.es_activo_fijo && it.puede_notificar)
      .flatMap((it) => (it.activos || []).filter((a) => !a.avisado && a.codigo_activo).map((a) => a.id));
    setUnidadesAvisarSeleccionadas(unidades);

    setFechaRetiro("");
    setHoraRetiro("");
    setMensajeRetiro(mensajeRetiroPara("", ""));
    setBusquedaAvisar("");
    setProveedorAvisar(null);
    setError("");
    setModalRetiroAbierto(true);
  };

  // El día y la hora se eligen con los selectores del navegador y el mensaje
  // se arma solo (se puede seguir editando a mano después).
  const cambiarFechaHoraRetiro = (fecha, hora) => {
    setFechaRetiro(fecha);
    setHoraRetiro(hora);
    setMensajeRetiro(mensajeRetiroPara(fecha, hora));
  };

  const alternarUnidadAvisar = (activoId) => {
    setUnidadesAvisarSeleccionadas((prev) =>
      prev.includes(activoId) ? prev.filter((i) => i !== activoId) : [...prev, activoId]
    );
  };

  const enviarAvisoRetiro = async () => {
    // El solicitante tiene que saber CUÁNDO venir: día y hora son obligatorios.
    if (!fechaRetiro || !horaRetiro) {
      setError("Elige el día y la hora en que el solicitante puede venir a retirar.");
      return;
    }
    if (!mensajeRetiro.trim()) {
      setError("Escribe el mensaje que se le enviará al solicitante.");
      return;
    }
    const momentoRetiro = new Date(`${fechaRetiro}T${horaRetiro}:00`);
    if (momentoRetiro.getTime() < Date.now()) {
      setError("La fecha/hora de retiro que elegiste ya pasó — elige un momento futuro.");
      return;
    }
    const items = Object.entries(cantidadesAvisar)
      .filter(([, cantidad]) => Number(cantidad) > 0)
      .map(([solicitud_item_id, cantidad]) => ({
        solicitud_item_id: Number(solicitud_item_id),
        cantidad: Number(cantidad),
      }));
    if (items.length === 0 && unidadesAvisarSeleccionadas.length === 0) {
      setError("Selecciona al menos un artículo o unidad para notificar.");
      return;
    }
    for (const i of items) {
      const it = solicitud.items.find((x) => x.id === i.solicitud_item_id);
      const pendiente = it ? cantidadRelevante(it) - (it.cantidad_lista || 0) : 0;
      if (it && i.cantidad > pendiente) {
        setError(`No puedes notificar más de lo pendiente en "${it.descripcion_producto}" (pendiente: ${Math.max(pendiente, 0)}).`);
        return;
      }
    }
    // Antes de avisar, confirmar que lo que se avisa como producto normal de
    // verdad no es un activo fijo (los activos se etiquetan y entregan aparte).
    // Si resulta que sí es un activo, se puede marcar ahí mismo en vez de
    // tener que cancelar y buscar el ítem de nuevo.
    const itemsNormales = items
      .map((i) => {
        const it = solicitud.items.find((x) => x.id === i.solicitud_item_id);
        // Si ya se marcó como activo fijo (por ejemplo, justo acá abajo en
        // esta misma ventana en un intento anterior), ya no hace falta
        // volver a preguntar por él.
        // Tampoco si ya se notificó antes como producto normal: un producto es una cosa o la otra.
        return it && !it.es_activo_fijo && !(Number(it.cantidad_lista) > 0)
          ? { id: it.id, descripcion: it.descripcion_producto, cantidad: i.cantidad }
          : null;
      })
      .filter(Boolean);
    if (itemsNormales.length > 0) {
      setConfirmandoNoActivos({ items: itemsNormales, activoIds: unidadesAvisarSeleccionadas });
      return;
    }
    await enviarAvisoRetiroConfirmado(items, unidadesAvisarSeleccionadas);
  };

  const enviarAvisoRetiroConfirmado = async (items, activoIds) => {
    setEnviandoRetiro(true);
    setError("");
    try {
      await apiFetch(`/api/solicitudes/${id}/listo-para-retiro`, {
        method: "POST",
        body: JSON.stringify({
          mensaje: mensajeRetiro,
          fecha_retiro: fechaRetiro,
          hora_retiro: horaRetiro,
          items,
          activo_ids: activoIds,
        }),
      });
      setModalRetiroAbierto(false);
      setConfirmandoNoActivos(null);
      cargar();
    } catch (err) {
      setError(err.message);
    } finally {
      setEnviandoRetiro(false);
    }
  };

  const [modalEntregaAbierto, setModalEntregaAbierto] = useState(false);
  const [recibidoPor, setRecibidoPor] = useState("");
  const [recibeSolicitante, setRecibeSolicitante] = useState(true);
  // Si recibe otra persona: se busca entre las registradas (recibidoPorId) o,
  // si es alguien nuevo del todo, se escribe el nombre a mano (recibeNuevo).
  const [recibidoPorId, setRecibidoPorId] = useState("");
  const [recibeNuevo, setRecibeNuevo] = useState(false);
  const [busquedaEntrega, setBusquedaEntrega] = useState("");
  const [cantidadesEntrega, setCantidadesEntrega] = useState({});
  const [registrandoEntrega, setRegistrandoEntrega] = useState(false);

  const abrirModalEntrega = () => {
    const iniciales = {};
    solicitud.items.forEach((it) => {
      if (it.es_activo_fijo) return;
      const disponible = it.cantidad_lista - it.cantidad_entregada_total;
      if (disponible > 0) iniciales[it.id] = disponible;
    });
    setCantidadesEntrega(iniciales);
    setRecibeSolicitante(true);
    setRecibidoPor(solicitud.solicitante_nombre);
    setRecibidoPorId("");
    setRecibeNuevo(false);
    setBusquedaEntrega("");
    setModalEntregaAbierto(true);
  };

  const alternarRecibeSolicitante = (marcar) => {
    setRecibeSolicitante(marcar);
    setRecibidoPor(marcar ? solicitud.solicitante_nombre : "");
    setRecibidoPorId("");
    setRecibeNuevo(false);
  };

  // Eligió a alguien de la lista: se guarda su nombre como quien recibe.
  const elegirQuienRecibe = (usuarioId) => {
    setRecibidoPorId(usuarioId);
    const persona = usuarios.find((u) => String(u.id) === String(usuarioId));
    setRecibidoPor(persona ? persona.nombre : "");
  };

  // --- OPCIÓN A IMPLEMENTADA CON TU CÓDIGO ORIGINAL ---
  const registrarEntrega = async () => {
    if (!recibidoPor.trim()) {
      setError("Indica el nombre de quién recibe el producto.");
      return;
    }
    const items = Object.entries(cantidadesEntrega)
      .filter(([, cantidad]) => Number(cantidad) > 0)
      .map(([solicitud_item_id, cantidad_entregada]) => ({
        solicitud_item_id: Number(solicitud_item_id),
        cantidad_entregada: Number(cantidad_entregada),
      }));
    if (items.length === 0) {
      setError("Indica al menos una cantidad a entregar.");
      return;
    }
    for (const i of items) {
      const it = solicitud.items.find((x) => x.id === i.solicitud_item_id);
      const disponible = it ? it.cantidad_lista - it.cantidad_entregada_total : 0;
      if (it && i.cantidad_entregada > disponible) {
        setError(`No puedes entregar más de lo disponible en "${it.descripcion_producto}" (disponible: ${Math.max(disponible, 0)}).`);
        return;
      }
    }
    setRegistrandoEntrega(true);
    setError("");
    try {
      // 1. Guardar la entrega en BD
      const resultado = await apiFetch(`/api/solicitudes/${id}/entregar`, {
        method: "POST",
        body: JSON.stringify({ recibido_por: recibidoPor, items }),
      });
      setModalEntregaAbierto(false);

      // 2. Refrescar datos en pantalla
      cargar();

      // 3. Generar y abrir PDF del Acta de ESTA entrega puntual (no de todo
      // lo que ya está listo en la solicitud, que puede incluir entregas previas)
      await abrirArchivoProtegido(`/api/solicitudes/${id}/acta?acta_id=${resultado.acta_id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setRegistrandoEntrega(false);
    }
  };

  // Unidades de activo listas para entregar: ya avisadas (esa unidad en
  // particular, no todo el ítem) y todavía sin marcar como entregadas.
  const unidadesActivoEntregables = () =>
    (solicitud.items || []).flatMap((it) =>
      it.es_activo_fijo
        ? (it.activos || [])
            .filter((a) => a.avisado && !a.entregado)
            .map((a) => ({ ...a, descripcion_producto: it.descripcion_producto }))
        : []
    );

  const [modalEntregaActivosAbierto, setModalEntregaActivosAbierto] = useState(false);
  const [activosSeleccionadosEntrega, setActivosSeleccionadosEntrega] = useState([]);
  const [busquedaEntregaActivos, setBusquedaEntregaActivos] = useState("");
  const [registrandoEntregaActivos, setRegistrandoEntregaActivos] = useState(false);
  const [custodioEsSolicitante, setCustodioEsSolicitante] = useState(true);

  const abrirModalEntregaActivos = () => {
    setActivosSeleccionadosEntrega(unidadesActivoEntregables().map((a) => a.id));
    setBusquedaEntregaActivos("");
    setCustodioEsSolicitante(true);
    setCustodioId(String(solicitud.solicitante_id));
    setModalEntregaActivosAbierto(true);
  };

  const alternarCustodioEsSolicitante = (marcar) => {
    setCustodioEsSolicitante(marcar);
    setCustodioId(marcar ? String(solicitud.solicitante_id) : "");
  };

  const alternarActivoEntrega = (activoId) => {
    setActivosSeleccionadosEntrega((prev) =>
      prev.includes(activoId) ? prev.filter((i) => i !== activoId) : [...prev, activoId]
    );
  };

  // Genera el Acta de Recepción de Bienes Etiquetados (distinta del Acta de
  // Entrega de productos normales), con solo las unidades de esta entrega.
  const registrarEntregaActivos = async () => {
    if (!custodioId) {
      setError("Selecciona el responsable que recibe los bienes.");
      return;
    }
    if (activosSeleccionadosEntrega.length === 0) {
      setError("Selecciona al menos una unidad a entregar.");
      return;
    }
    setRegistrandoEntregaActivos(true);
    setError("");
    try {
      const resultado = await apiFetch(`/api/solicitudes/${id}/entregar-activos`, {
        method: "POST",
        body: JSON.stringify({ custodio_id: custodioId, activo_ids: activosSeleccionadosEntrega }),
      });
      setModalEntregaActivosAbierto(false);
      cargar();
      await abrirArchivoProtegido(`/api/solicitudes/${id}/acta-bienes?acta_id=${resultado.acta_id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setRegistrandoEntregaActivos(false);
    }
  };

  const generarActa = async () => {
    setError("");
    try {
      await abrirArchivoProtegido(`/api/solicitudes/${id}/acta`);
    } catch (err) {
      setError(err.message);
    }
  };

  // Aviso corto de confirmación (ej. al elegir una cotización, que antes no
  // mostraba nada y parecía que el clic no había funcionado).
  const [exito, setExito] = useState("");
  // Se guarda el temporizador para cancelarlo: si no, el del aviso anterior
  // borraba antes de tiempo el siguiente al elegir varias cotizaciones seguidas.
  const temporizadorExito = useRef(null);
  const avisarExito = (texto) => {
    clearTimeout(temporizadorExito.current);
    setExito(texto);
    temporizadorExito.current = setTimeout(() => setExito(""), 3500);
  };

  const seleccionarCotizacion = async (cotizacionId) => {
    try {
      await apiFetch(`/api/cotizaciones/${cotizacionId}/seleccionar`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      const elegida = solicitud.cotizaciones.find((c) => c.id === cotizacionId);
      avisarExito(`Cotización de ${elegida?.proveedor_nombre || "el proveedor"} elegida como recomendada.`);
      cargar();
    } catch (err) {
      setError(err.message);
    }
  };

  const eliminarCotizacion = async (cotizacionId) => {
    if (!window.confirm("¿Eliminar esta cotización?")) return;
    try {
      await apiFetch(`/api/cotizaciones/${cotizacionId}/eliminar`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      cargar();
    } catch (err) {
      setError(err.message);
    }
  };

  const alternarItemAbierto = (itemId) => {
    setItemsAbiertos((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const actualizarFormActivo = (itemId, campo, valor) => {
    setFormsActivo((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], [campo]: valor },
    }));
  };

  const actualizarUnidadActivo = (itemId, indice, campo, valor) => {
    setFormsActivo((prev) => {
      const form = prev[itemId];
      const unidades = form.unidades.map((u, i) => (i === indice ? { ...u, [campo]: valor } : u));
      return { ...prev, [itemId]: { ...form, unidades } };
    });
  };

  // Ya se notificó (o entregó) como producto normal: no se puede volver activo fijo.
  const yaVaComoNormal = (it) => !it.es_activo_fijo && Number(it.cantidad_lista) > 0;

  const marcarTodosActivoFijo = (marcar) => {
    setFormsActivo((prev) => {
      const nuevo = { ...prev };
      solicitud.items.forEach((it) => {
        if (marcar && yaVaComoNormal(it)) return;
        const actual = nuevo[it.id] || formActivoVacio(cantidadRelevante(it));
        nuevo[it.id] = { ...actual, es_activo_fijo: marcar };
      });
      return nuevo;
    });
    // La casilla y la pregunta (llenar ahora o después) están dentro del detalle de cada producto:
    // se abren todos para que se vea el cambio y se pueda terminar de guardar.
    setItemsAbiertos((prev) => {
      const abiertos = { ...prev };
      solicitud.items.forEach((it) => {
        abiertos[it.id] = true;
      });
      return abiertos;
    });
  };

  const guardarActivo = async (itemId) => {
    const form = formsActivo[itemId];
    setError("");
    try {
      await apiFetch(`/api/items/${itemId}/activo`, {
        method: "POST",
        body: JSON.stringify({ es_activo_fijo: form.es_activo_fijo, activos: form.unidades }),
      });
      cargar();
    } catch (err) {
      setError(err.message);
    }
  };

  // "Llenar después": se guarda de una vez que es activo fijo (para que no se
  // pierda si compras navega a otro lado), pero sin pedir los datos de cada
  // unidad todavía — esos se completan luego desde la pantalla dedicada.
  const marcarActivoParaDespues = async (itemId) => {
    setError("");
    try {
      await apiFetch(`/api/items/${itemId}/activo`, {
        method: "POST",
        body: JSON.stringify({ es_activo_fijo: true, activos: [] }),
      });
      cargar();
    } catch (err) {
      setError(err.message);
    }
  };

  // Editar el detalle de un producto — para cuando la cotización real que
  // manda el proveedor no viene exactamente igual a lo que pidió el
  // solicitante (otra marca, otra medida, etc.) y compras necesita
  // corregirlo para que quede igual a lo que en verdad se va a comprar.
  const abrirEdicionItem = (item, e) => {
    e.stopPropagation();
    setEditandoItemId(item.id);
    setFormEditarItem({
      descripcion_producto: item.descripcion_producto || "",
      descripcion_detallada: item.descripcion_detallada || "",
      cantidad: item.cantidad,
      unidad: item.unidad || "",
    });
    setItemsAbiertos((prev) => ({ ...prev, [item.id]: true }));
    setError("");
  };

  const cancelarEdicionItem = (e) => {
    e?.stopPropagation();
    setEditandoItemId(null);
  };

  const guardarEdicionItem = async (itemId, e) => {
    e?.stopPropagation();
    setGuardandoItem(true);
    setError("");
    try {
      await apiFetch(`/api/items/${itemId}/editar`, {
        method: "POST",
        body: JSON.stringify(formEditarItem),
      });
      setEditandoItemId(null);
      cargar();
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardandoItem(false);
    }
  };

  const crearCustodio = async () => {
    if (!nuevoCustodio.nombre.trim() || !nuevoCustodio.correo.trim()) {
      setError("Nombre y correo del responsable son obligatorios.");
      return;
    }
    setCreandoCustodio(true);
    setError("");
    try {
      const creado = await apiFetch("/api/usuarios/persona-referencia", {
        method: "POST",
        body: JSON.stringify(nuevoCustodio),
      });
      const listaActualizada = await apiFetch("/api/usuarios");
      setUsuarios(listaActualizada || []);
      setCustodioId(String(creado.id));
      setNuevoCustodio(custodioVacio);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreandoCustodio(false);
    }
  };

  const generarActaBienes = async () => {
    if (!custodioId) {
      setError("Selecciona o crea el responsable del bien.");
      return;
    }
    setError("");
    try {
      await abrirArchivoProtegido(`/api/solicitudes/${id}/acta-bienes?custodio_id=${custodioId}`);
    } catch (err) {
      setError(err.message);
    }
  };

  const renderDetalleItem = (item) => {

    if (editandoItemId === item.id) {
      return (
        <div style={styles.formEditarItem} onClick={(e) => e.stopPropagation()}>
          <Ayuda>
          <p style={{ fontSize: "0.8rem", color: "#5b6472", margin: "0 0 0.6rem" }}>
            Ajusta esto para que quede igual a lo que en realidad se va a comprar (ej. si la
            cotización del proveedor viene con otra marca o medida).
          </p>
          </Ayuda>
          <div className="form-grid" style={styles.gridEditarItem}>
            <textarea
              ref={descripcionEditarItemRef}
              rows={1}
              placeholder="Descripción del producto"
              value={formEditarItem.descripcion_producto}
              onChange={(e) => setFormEditarItem({ ...formEditarItem, descripcion_producto: e.target.value })}
              style={{ ...styles.input, resize: "none", overflow: "hidden" }}
            />
            <input
              type="number"
              min="1"
              placeholder="Cantidad"
              value={formEditarItem.cantidad}
              onChange={(e) => setFormEditarItem({ ...formEditarItem, cantidad: e.target.value })}
              style={styles.input}
            />
            <input
              type="text"
              placeholder="Unidad"
              value={formEditarItem.unidad}
              onChange={(e) => setFormEditarItem({ ...formEditarItem, unidad: e.target.value })}
              style={styles.input}
            />
          </div>
          <textarea
            ref={descripcionDetalladaEditarItemRef}
            placeholder="Descripción detallada (opcional)"
            value={formEditarItem.descripcion_detallada}
            onChange={(e) => setFormEditarItem({ ...formEditarItem, descripcion_detallada: e.target.value })}
            rows={2}
            style={{ ...styles.input, width: "100%", marginTop: "0.5rem", resize: "none", overflowY: "auto", maxHeight: 320 }}
          />
          {error && <p style={{ color: "#a3402f", fontSize: "0.82rem", marginTop: "0.4rem" }}>{error}</p>}
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.6rem" }}>
            <button
              type="button"
              disabled={guardandoItem}
              onClick={(e) => guardarEdicionItem(item.id, e)}
              style={styles.botonPequenoExito}
            >
              {guardandoItem ? "Guardando..." : "💾 Guardar"}
            </button>
            <button type="button" onClick={cancelarEdicionItem} style={styles.botonPequeno}>
              Cancelar
            </button>
          </div>
        </div>
      );
    }

    // La "descripción detallada" es un campo aparte que llena quien solicita —
    // si escribió lo mismo que ya dice el título del ítem, mostrarla de nuevo
    // no agrega nada, solo se ve como texto duplicado.
    const detalleEsRedundante =
      !item.descripcion_detallada ||
      item.descripcion_detallada.trim().toLowerCase() === item.descripcion_producto.trim().toLowerCase();

    return (
      <>
        {!detalleEsRedundante && (
          <>
            <button
              type="button"
              onClick={() =>
                setDetalleProductoAbierto((prev) => ({ ...prev, [item.id]: !prev[item.id] }))
              }
              style={{ ...styles.botonPequeno, marginBottom: "0.5rem" }}
            >
              {detalleProductoAbierto[item.id] ? "▲ Ocultar detalles del producto" : "▼ Ver más detalles del producto"}
            </button>
            {detalleProductoAbierto[item.id] && (
              <p style={{ fontSize: "0.85rem", color: "#3d4a5c", margin: "0 0 0.6rem" }}>
                {item.descripcion_detallada}
              </p>
            )}
          </>
        )}

        {esCompras && solicitud.estado === "en_proceso_compra" && (() => {
          const form = formsActivo[item.id] || formActivoVacio(cantidadRelevante(item));
          const guardadoComoActivo = !!item.es_activo_fijo;
          const faltanDatosGuardados = activoIncompleto(item);
          const mostrarFormularioCompleto = !!llenandoActivoAhora[item.id];
          // Recién tildado, todavía sin guardar nada ni elegir un camino —
          // se pregunta si se llenan los datos ahí mismo o después.
          const mostrarEleccion = form.es_activo_fijo && !guardadoComoActivo && !mostrarFormularioCompleto;
          const mostrarFormularioUnidades = mostrarFormularioCompleto;
          const necesitaGuardar = mostrarFormularioCompleto || form.es_activo_fijo !== guardadoComoActivo;

          return (
            <div style={styles.formActivo}>
              {yaVaComoNormal(item) ? (
                <p style={{ margin: 0, fontSize: "0.82rem", color: "#5b6472" }}>
                  📦 Ya se notificó como producto normal ({item.cantidad_lista} de {cantidadRelevante(item)}): no se puede
                  marcar como activo fijo.
                </p>
              ) : (
              <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.85rem" }}>
                <input
                  type="checkbox"
                  checked={form.es_activo_fijo}
                  onChange={(e) => {
                    actualizarFormActivo(item.id, "es_activo_fijo", e.target.checked);
                    if (!e.target.checked) {
                      setLlenandoActivoAhora((prev) => ({ ...prev, [item.id]: false }));
                    }
                  }}
                />
                Es activo fijo (necesita etiquetarse)
              </label>
              )}

              {mostrarEleccion && (
                <div style={styles.eleccionActivo}>
                  <p style={{ fontSize: "0.8rem", color: "#5b6472", margin: "0 0 0.5rem" }}>
                    ¿Llenamos el código, marca y demás datos de cada unidad ahora, o queda pendiente por ahora?
                  </p>
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={() => setLlenandoActivoAhora((prev) => ({ ...prev, [item.id]: true }))}
                      style={styles.botonPequenoExito}
                    >
                      📝 Llenar datos ahora
                    </button>
                    <button type="button" onClick={() => marcarActivoParaDespues(item.id)} style={styles.botonPequeno}>
                      🕒 Llenar después
                    </button>
                  </div>
                </div>
              )}

              {guardadoComoActivo && faltanDatosGuardados && !mostrarFormularioCompleto && (
                <div style={styles.eleccionActivo}>
                  <p style={{ fontSize: "0.8rem", color: "#9c7a1a", margin: "0 0 0.5rem" }}>
                    ⚠ Todavía faltan los datos (código, marca, etc.) de alguna unidad.
                  </p>
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={() => setLlenandoActivoAhora((prev) => ({ ...prev, [item.id]: true }))}
                      style={styles.botonPequeno}
                    >
                      📝 Llenar datos aquí
                    </button>
                    <Link
                      to={`/solicitud/${id}/activo/${item.id}`}
                      style={{ ...styles.botonPequeno, textDecoration: "none", display: "inline-block" }}
                    >
                      🧾 Ir a la pantalla de datos de activo
                    </Link>
                  </div>
                </div>
              )}

              {guardadoComoActivo && !faltanDatosGuardados && !mostrarFormularioCompleto && (
                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginTop: "0.5rem", fontSize: "0.8rem", color: "#2f7a54" }}>
                  ✅ Datos de activo completos
                  <Link to={`/solicitud/${id}/activo/${item.id}`} style={{ fontSize: "0.78rem" }}>
                    Editar
                  </Link>
                </div>
              )}

              {mostrarFormularioUnidades &&
                form.unidades.map((u, i) => {
                  const registrado = (item.activos || []).find((a) => a.numero_unidad === u.numero_unidad);
                  return (
                    <div key={u.numero_unidad} style={styles.tarjetaUnidadActivo}>
                      <p style={{ fontSize: "0.8rem", fontWeight: 600, margin: "0 0 0.4rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        Activo {u.numero_unidad} de {cantidadRelevante(item)}
                        {!!registrado?.entregado && (
                          <span style={styles.badgeEntregado}>✅ Entregado a {registrado.entregado_a}</span>
                        )}
                      </p>
                      <div className="form-grid" style={styles.itemGridCustodio}>
                        <input
                          type="text"
                          placeholder="Cod. Inventario"
                          value={u.codigo_activo}
                          onChange={(e) => actualizarUnidadActivo(item.id, i, "codigo_activo", e.target.value)}
                          style={styles.input}
                        />
                        <input
                          type="text"
                          placeholder="Marca"
                          value={u.marca}
                          onChange={(e) => actualizarUnidadActivo(item.id, i, "marca", e.target.value)}
                          style={styles.input}
                        />
                        <input
                          type="text"
                          placeholder="Modelo"
                          value={u.modelo}
                          onChange={(e) => actualizarUnidadActivo(item.id, i, "modelo", e.target.value)}
                          style={styles.input}
                        />
                        <input
                          type="text"
                          placeholder="Serie"
                          value={u.numero_serie}
                          onChange={(e) => actualizarUnidadActivo(item.id, i, "numero_serie", e.target.value)}
                          style={styles.input}
                        />
                        <input
                          type="date"
                          value={u.fecha_adquisicion}
                          onChange={(e) => actualizarUnidadActivo(item.id, i, "fecha_adquisicion", e.target.value)}
                          style={styles.input}
                        />
                        <select
                          value={u.edificio_id}
                          onChange={(e) => actualizarUnidadActivo(item.id, i, "edificio_id", e.target.value)}
                          style={styles.input}
                        >
                          <option value="">Edificio de destino...</option>
                          {edificios.map((ed) => (
                            <option key={ed.id} value={ed.id}>
                              {ed.nombre}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
              {necesitaGuardar && (
                <button type="button" onClick={() => guardarActivo(item.id)} style={{ ...styles.botonPequeno, marginTop: "0.5rem" }}>
                  💾 Guardar datos de activo
                </button>
              )}
            </div>
          );
        })()}
      </>
    );
  };

  if (cargando) return <Cargando />;
  if (error && !solicitud) return <p style={{ color: "#a3402f" }}>{error}</p>;
  if (!solicitud) return null;


  // comentario_aprobador lo escribe quien decide: al aprobar en parte explica lo que no aprobó (se ve
  // al pasar el mouse sobre la cantidad rechazada); al rechazar todo es el motivo, y se sigue viendo
  // como aviso si Compras la reactiva.
  const aprobadoresConFecha = (solicitud.aprobadores || []).filter((a) => a.aprobado_en);
  const autorComentario =
    (solicitud.aprobadores || []).find((a) => a.id === solicitud.aprobador_id) ||
    [...aprobadoresConFecha].sort((x, y) => new Date(y.aprobado_en) - new Date(x.aprobado_en))[0] ||
    (solicitud.aprobadores || [])[0];
  const comentarioEsDeRechazo = solicitud.estado === "rechazada" || !autorComentario;
  const comentarioDeAprobacion = !comentarioEsDeRechazo ? solicitud.comentario_aprobador : null;
  const motivoRechazo = comentarioEsDeRechazo ? solicitud.comentario_aprobador : null;

  // ---------- Datos derivados para la barra lateral (solo staff) ----------
  // Lo que un proveedor COTIZÓ no cambia aunque gerencia elija a otro: los productos que se le
  // quitaron vienen aparte (items_descartados) y siguen contando aquí.
  const idsCotizadosDe = (c) => new Set([...c.items.map((ci) => ci.id), ...(c.items_descartados || []).map((d) => d.id)]);
  const cotizoProducto = (c, itemId) => idsCotizadosDe(c).has(itemId);
  // Monto a mostrar: el aprobado si se eligió; si no, lo que cotizó originalmente.
  const montoMostrado = (c) => (c.seleccionada ? Number(c.monto_total) : Number(c.monto_cotizado ?? c.monto_total));
  // Ya se decidió (la solicitud pasó de cotización): la elegida resalta y las demás pasan a segundo plano.
  const decisionTomada = !["nueva", "en_cola"].includes(solicitud.estado);
  const noElegida = (c) => !c.seleccionada && (decisionTomada || estadoDeCotizacion(c).texto === "No elegida");
  const proveedoresResumen = Object.values(
    (solicitud.cotizaciones || []).reduce((acc, c) => {
      if (!acc[c.proveedor_nombre]) {
        acc[c.proveedor_nombre] = { nombre: c.proveedor_nombre, sumas: {}, seleccionada: false };
      }
      const monedaCot = c.moneda === "USD" ? "USD" : "NIO";
      acc[c.proveedor_nombre].sumas[monedaCot] = (acc[c.proveedor_nombre].sumas[monedaCot] || 0) + montoMostrado(c);
      if (c.seleccionada) acc[c.proveedor_nombre].seleccionada = true;
      return acc;
    }, {})
  );
  const montoEstimado = sumarPorMoneda(
    (solicitud.cotizaciones || []).filter((c) => c.seleccionada).map((c) => ({ moneda: c.moneda, monto: c.monto_total }))
  );
  // Estado de cada cotización según los productos que cubre — así Compras distingue las que ya se
  // aprobaron (historial) de las nuevas que se subieron para lo que todavía falta.
  const estadoDeCotizacion = (c) => {
    const productos = c.items.map((ci) => (solicitud.items || []).find((it) => it.id === ci.id)).filter(Boolean);
    const aprobado = (it) => Number(it.cantidad_aprobada) > 0 || !!it.aprobado;
    const conAprobacion = productos.filter(aprobado);
    const todosAprobados = productos.length > 0 && conAprobacion.length === productos.length;
    if (c.seleccionada && todosAprobados) return { texto: "✓ Aprobada", color: "#2f7a54", bg: "#dcf0e6", historial: true };
    if (c.seleccionada && conAprobacion.length > 0)
      return { texto: "✓ Aprobada en parte", color: "#2f7a54", bg: "#dcf0e6", historial: true };
    if (c.seleccionada) return { texto: "⏳ Esperando aprobación", color: "#b85a0b", bg: "#fde6cf", historial: false };
    if (todosAprobados) return { texto: "No elegida", color: "#5b6472", bg: "#eef1f3", historial: true };
    return { texto: "Por elegir", color: "#1d5fa8", bg: "#d7e8f7", historial: false };
  };
  const cotizacionesOrdenadas = (() => {
    const lista = (solicitud.cotizaciones || []).map((c) => ({ c, est: estadoDeCotizacion(c) }));
    // Mientras se sigue cotizando, primero las que faltan por decidir y después el historial.
    return solicitud.estado === "en_cola" ? [...lista].sort((a, b) => Number(a.est.historial) - Number(b.est.historial)) : lista;
  })();
  const hayHistorial = cotizacionesOrdenadas.some((x) => x.est.historial);
  const hayPendientes = cotizacionesOrdenadas.some((x) => !x.est.historial);
  const proveedorSeleccionadoTexto = proveedoresResumen
    .filter((p) => p.seleccionada)
    .map((p) => p.nombre)
    .join(", ");

  const historialDesc = [...(solicitud.historial_estados || [])].reverse();
  const historialVisible = historialAbierto ? historialDesc : historialDesc.slice(0, 5);

  const itemsFiltrados = solicitud.items || [];

  // El precio unitario solo se escribe mientras compras arma una cotización; después
  // el precio sale de la cotización elegida y la columna sobra.
  // La última columna solo trae el botón "Editar" (Compras, mientras cotiza).
  const mostrarAccionesItem = esCompras && solicitud.estado === "en_cola";

  // Datos de apoyo de un producto (cotización elegida, proveedor sugerido y archivo
  // de referencia): ya no ensucian la fila, salen al abrir el detalle del producto.
  const datosApoyoItem = (item) =>
    (item.cotizacion_elegida || item.proveedor_referencia_nombre || item.archivo_referencia) && (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem 1.4rem", fontSize: "0.8rem", color: "#5b6472", marginBottom: "0.5rem" }}>
        {item.cotizacion_elegida && (
          <span>
            Cotización:{" "}
            <strong>
              {(item.proveedores_elegidos || []).length > 1 ? proveedorDeItem(item) : item.cotizacion_elegida.proveedor_nombre}
            </strong>{" "}
            <BadgeFormaPago valor={item.cotizacion_elegida.forma_pago} />
          </span>
        )}
        {item.proveedor_referencia_nombre && <span>💡 Proveedor sugerido: {item.proveedor_referencia_nombre}</span>}
      </div>
    );

  // La fila se despliega solo si hay algo que no se ve ya en ella (la foto sale en
  // miniatura y las cotizaciones en su columna).
  const itemTieneDetalle = (item) =>
    editandoItemId === item.id ||
    !!item.cotizacion_elegida ||
    !!item.proveedor_referencia_nombre ||
    (!!item.descripcion_detallada &&
      item.descripcion_detallada.trim().toLowerCase() !== item.descripcion_producto.trim().toLowerCase()) ||
    (esCompras && solicitud.estado === "en_proceso_compra");

  const renderListaItemsStaff = () => (
    <div style={{ overflowX: "auto" }}>
      <table className="tabla-items-staff" style={styles.tabla}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "1px solid #d3d9df" }}>
            <th style={styles.th}>Producto</th>
            <th style={{ ...styles.th, textAlign: "center" }}>Cantidad solicitada</th>
            <th style={{ ...styles.th, textAlign: "center" }}>Cantidad rechazada</th>
            <th style={{ ...styles.th, textAlign: "center" }}>Cantidad aprobada</th>
            <th style={{ ...styles.th, textAlign: "center" }}>Cotizaciones</th>
            <th style={styles.th}>Prioridad</th>
            <th style={styles.th}>Estado</th>
            {mostrarAccionesItem && <th style={styles.th}></th>}
          </tr>
        </thead>
        <tbody>
          {itemsFiltrados.map((item) => {
            const estadoItemMostrado = estadoItemVisible(item, solicitud);
            const desplegable = itemTieneDetalle(item);
            const abierto = desplegable && !!itemsAbiertos[item.id];
            // Una vez que ya se entregó todo lo solicitado de este ítem, ese
            // es su estado final — no tiene sentido seguir mostrando
            // "Listo"/"Aprobado" como si todavía estuviera en trámite.
            const totalmenteEntregado = itemEntregado(item);
            // Cantidades para las columnas "aprobada" y "rechazada". Antes de que el
            // gerente decida se muestra "—".
            const sinAprobar = Math.max(0, Number(item.cantidad) - Number(item.cantidad_aprobada || 0));
            const decidido = !!item.descartado || Number(item.cantidad_aprobada) > 0 || !!item.aprobado;
            const cantidadRechazada = item.descartado
              ? Math.min(sinAprobar, Number(item.cantidad_descartada) || sinAprobar)
              : Math.min(sinAprobar, Number(item.cantidad_descartada) || 0);
            const unidadTxt = item.unidad ? ` ${item.unidad}` : "";
            const colorBorde = totalmenteEntregado
              ? "#2f7a54"
              : mapaEstadosItem()[estadoItemMostrado]?.color || "#d3d9df";

            return (
              <React.Fragment key={item.id}>
                <tr
                  style={{ borderBottom: "1px solid #eef1f3", cursor: desplegable ? "pointer" : "default" }}
                  onClick={desplegable ? () => alternarItemAbierto(item.id) : undefined}
                  title={desplegable ? (abierto ? "Ocultar detalles" : "Ver detalles del producto") : undefined}
                >
                  <td style={{ ...styles.td, borderLeft: `4px solid ${colorBorde}`, textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                      {item.archivo_referencia && <MiniaturaArchivo nombreArchivo={item.archivo_referencia} />}
                      <div style={{ fontWeight: 600, color: "var(--tema-titulo, #0b3c60)" }}>
                        {item.numero_item}. {item.descripcion_producto}
                      </div>
                      {desplegable && (
                        <span style={{ marginLeft: "auto", color: "#8993a1", fontSize: "0.8rem" }}>{abierto ? "▴" : "▾"}</span>
                      )}
                    </div>
                  </td>
                  <td style={{ ...styles.td, textAlign: "center", whiteSpace: "nowrap" }}>
                    {item.cantidad}
                    {unidadTxt}
                  </td>
                  <td style={{ ...styles.td, textAlign: "center", whiteSpace: "nowrap" }}>
                    {decidido && cantidadRechazada > 0 ? (
                      <NotaEmergente titulo="Motivo del rechazo" texto={comentarioDeAprobacion}>
                        <span style={{ fontWeight: 700, color: "#a3402f" }}>
                          {cantidadRechazada}
                          {unidadTxt}
                        </span>
                      </NotaEmergente>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td style={{ ...styles.td, textAlign: "center", whiteSpace: "nowrap" }}>
                    {decidido ? (
                      <span style={{ fontWeight: 700, color: Number(item.cantidad_aprobada) > 0 ? "#2f7a54" : "#8993a1" }}>
                        {Number(item.cantidad_aprobada) || 0}
                        {unidadTxt}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td style={{ ...styles.td, textAlign: "center" }}>
                    {(() => {
                      // Cotizaciones que ya incluyen este producto (máximo 3 por producto).
                      const cotsDelItem = (solicitud.cotizaciones || []).filter((c) =>
                        cotizoProducto(c, item.id)
                      );
                      if (item.descartado && !(Number(item.cantidad_aprobada) > 0)) return "—";
                      return cotsDelItem.length > 0 ? (
                        <>
                          <span style={{ color: "#2f7a54", fontWeight: 700 }}>
                            ✓ {cotsDelItem.length} de {parametro("max_cotizaciones_por_producto")}
                          </span>
                          <div style={{ fontSize: "0.72rem", color: "#5b6472" }}>
                            {cotsDelItem.map((c) => c.proveedor_nombre).join(", ")}
                          </div>
                        </>
                      ) : (
                        <span style={{ color: "#8993a1" }}>0 de {parametro("max_cotizaciones_por_producto")}</span>
                      );
                    })()}
                  </td>
                  <td style={styles.td}>
                    {item.prioridad === "urgente" ? (
                      <span style={styles.badgeUrgente}>🔴 Urgente</span>
                    ) : (
                      "Normal"
                    )}
                  </td>
                  <td style={styles.td}>
                    {item.descartado && !(Number(item.cantidad_aprobada) > 0) ? (
                      <span style={{ ...styles.badgeEntregado, background: "#f7ded9", color: "#a3402f" }}>
                        🚫 Rechazado
                      </span>
                    ) : totalmenteEntregado ? (
                      <span style={{ ...styles.badgeEntregado, background: "#dcf0e6", color: "#2f7a54" }}>
                        📦 Entregado
                      </span>
                    ) : (
                      <>
                        <Badge estado={estadoItemMostrado} mapa={mapaEstadosItem()} />
                        {estadoItemMostrado === "rechazada" &&
                          item.cantidad_descartada > 0 &&
                          item.cantidad_descartada < item.cantidad && (
                            <span style={{ fontSize: "0.76rem", color: "#a3402f" }}>
                              {" "}
                              ({item.cantidad_descartada} de {item.cantidad})
                            </span>
                          )}
                      </>
                    )}
                  </td>
                  {mostrarAccionesItem && (
                    <td style={styles.td}>
                      <button
                        type="button"
                        onClick={(e) => abrirEdicionItem(item, e)}
                        style={styles.botonPequeno}
                        title="Editar descripción, cantidad o unidad de este producto"
                      >
                        ✏️ Editar
                      </button>
                    </td>
                  )}
                </tr>
                {abierto && (
                  <tr>
                    <td
                      colSpan={7 + (mostrarAccionesItem ? 1 : 0)}
                      style={{ ...styles.td, background: "#f7f8fa", textAlign: "left" }}
                    >
                      {datosApoyoItem(item)}
                      {renderDetalleItem(item)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const itemsAccion = [];
  if (esCompras && solicitud.estado === "nueva") {
    itemsAccion.push({ key: "cola", icono: "▶️", texto: "Poner en cola", onClick: () => cambiarEstado("en_cola") });
  }
  if (
    esCompras &&
    solicitud.estado === "en_cola" &&
    !solicitud.en_atencion &&
    !solicitud.total_cotizaciones
  ) {
    itemsAccion.push({ key: "cotizando", icono: "🔍", texto: "Marcar como cotizando", onClick: marcarCotizando });
  }
  if (esCompras && solicitud.estado === "aprobada" && !solicitud.esperando_pago) {
    itemsAccion.push({
      key: "proceso",
      icono: "🛒",
      texto: "Iniciar proceso de compra",
      onClick: () => cambiarEstado("en_proceso_compra"),
    });
  }
  if (esCompras && solicitud.estado === "rechazada") {
    itemsAccion.push({ key: "reactivar", icono: "🔄", texto: "Reactivar solicitud", onClick: reactivarSolicitud });
  }
  if (
    esCompras &&
    // Solo después de que Compras inicia el proceso de compra.
    ["en_proceso_compra", "lista_para_retiro"].includes(solicitud.estado) &&
    solicitud.items.some((it) => it.puede_notificar && !it.listo_retiro)
  ) {
    itemsAccion.push({ key: "avisar", icono: "📦", texto: "Notificar retiro por correo", onClick: abrirModalRetiro });
  }
  if (
    esCompras &&
    ["en_cola", "aprobada", "en_proceso_compra", "lista_para_retiro"].includes(solicitud.estado) &&
    solicitud.items.some((it) => !it.es_activo_fijo && it.cantidad_lista - it.cantidad_entregada_total > 0)
  ) {
    itemsAccion.push({ key: "entregar", icono: "📋", texto: "Registrar entrega", onClick: abrirModalEntrega });
  }
  if (
    esCompras &&
    ["en_cola", "aprobada", "en_proceso_compra", "lista_para_retiro"].includes(solicitud.estado) &&
    unidadesActivoEntregables().length > 0
  ) {
    itemsAccion.push({
      key: "entregar-activos",
      icono: "📋",
      texto: "Registrar entrega de activos",
      onClick: abrirModalEntregaActivos,
    });
  }
  if (
    esStaff &&
    ["aprobada", "en_proceso_compra", "lista_para_retiro", "entregada"].includes(solicitud.estado) &&
    solicitud.entregas &&
    solicitud.entregas.length > 0
  ) {
    itemsAccion.push({ key: "acta", icono: "🧾", texto: "Generar Acta (PDF)", onClick: generarActa });
  }
  if (
    esCompras &&
    solicitud.items.some((it) => it.es_activo_fijo && (it.activos || []).some((a) => a.entregado))
  ) {
    itemsAccion.push({
      key: "acta-bienes",
      icono: "🏷️",
      texto:
        (solicitud.actas_bienes || []).length > 0
          ? "Actas de Bienes Etiquetados (reimprimir)"
          : "Generar Acta de Bienes Etiquetados",
      onClick: () => setMostrarActaBienes((v) => !v),
    });
  }

  // Cuando la compra ya terminó (todo entregado) no queda nada por accionar:
  // en vez de "Más acciones" solo se ofrece reimprimir las actas que ya existen.
  const solicitudFinalizada = solicitud.estado === "entregada";
  const pad4 = (n) => String(n).padStart(4, "0");
  // Las actas las reimprime solo el equipo (el solicitante no tiene permiso para el PDF).
  const actasParaReimprimir = !esStaff
    ? []
    : [
    ...((solicitud.actas_entrega || []).length > 0
      ? solicitud.actas_entrega.map((a) => ({
          key: "e" + a.id,
          texto: "Acta de entrega ACTA-" + pad4(a.numero_acta),
          detalle: a.recibido_por ? "Recibió: " + a.recibido_por : "",
          ruta: "/api/solicitudes/" + id + "/acta?acta_id=" + a.id,
        }))
      : (solicitud.entregas || []).length > 0
      ? [{ key: "e-general", texto: "Acta de entrega", detalle: "", ruta: "/api/solicitudes/" + id + "/acta" }]
      : []),
    // El acta de bienes etiquetados solo la puede generar compras.
    ...(esCompras
      ? (solicitud.actas_bienes || []).map((a) => ({
          key: "b" + a.id,
          texto: "Acta de bienes etiquetados ACTA-" + pad4(a.numero_acta),
          detalle: a.recibido_por ? "Recibió: " + a.recibido_por : "",
          ruta: "/api/solicitudes/" + id + "/acta-bienes?acta_id=" + a.id,
        }))
      : []),
  ];
  const reimprimirActa = async (ruta) => {
    setError("");
    try {
      await abrirArchivoProtegido(ruta);
    } catch (err) {
      setError(err.message);
    }
  };

  const renderActivosEntregados = () => {
        const activosEntregados = (solicitud.items || []).flatMap((it) =>
          (it.activos || [])
            .filter((a) => a.entregado)
            .map((a) => ({ ...a, descripcion_producto: it.descripcion_producto }))
        );
        if (activosEntregados.length === 0) return null;
        const termino = busquedaActivosEntregados.trim().toLowerCase();
        const visibles = termino
          ? activosEntregados.filter((a) =>
              [a.descripcion_producto, a.codigo_activo, a.marca, a.modelo, a.numero_serie, a.edificio_nombre, a.entregado_a]
                .join(" ")
                .toLowerCase()
                .includes(termino)
            )
          : activosEntregados;
        return (
          <div style={styles.activosEntregadosCard}>
            <h3 style={{ margin: "0 0 0.2rem", textAlign: "center" }}>
              🏷️ {esStaff ? "Activos entregados" : "Activos recibidos"}{" "}
              {(() => {
                // Unidades entregadas de las aprobadas, sumando todos los productos que son activo fijo.
                const totales = (solicitud.items || [])
                  .filter((it) => it.es_activo_fijo && (Number(it.cantidad_aprobada) > 0 || it.aprobado))
                  .reduce(
                    (t, it) => {
                      const e = entregaDe(it);
                      return { entregada: t.entregada + e.entregada, aprobada: t.aprobada + e.aprobada };
                    },
                    { entregada: 0, aprobada: 0 }
                  );
                if (!totales.aprobada) return <span style={styles.avisarContador}>{activosEntregados.length}</span>;
                const completo = totales.entregada >= totales.aprobada;
                return (
                  <span
                    style={{ ...styles.chipEntregaActivo, ...(completo ? styles.chipEntregaCompleta : styles.chipEntregaFalta), fontSize: "0.85rem", fontWeight: 800 }}
                    title={completo ? "Entregados todos" : `Faltan ${totales.aprobada - totales.entregada}`}
                  >
                    {totales.entregada} / {totales.aprobada}
                    {completo && " ✓"}
                  </span>
                );
              })()}
            </h3>
            <div style={styles.barraDorada} />
            <Ayuda>
            <p style={{ margin: "0 0 0.8rem", fontSize: "0.8rem", color: "#8993a1", textAlign: "center" }}>
              Cada fila es un activo con su código y la persona que lo recibió.
            </p>
            </Ayuda>
            {/* Con varios productos activo fijo, el detalle por producto ("2 / 5" = faltan 3). */}
            {(solicitud.items || []).filter((it) => it.es_activo_fijo && (Number(it.cantidad_aprobada) > 0 || it.aprobado)).length > 1 && (
            <div style={styles.chipsEntregaActivos}>
              {(solicitud.items || [])
                .filter((it) => it.es_activo_fijo && (Number(it.cantidad_aprobada) > 0 || it.aprobado))
                .map((it) => {
                  const { aprobada, entregada, falta } = entregaDe(it);
                  return (
                    <span
                      key={it.id}
                      style={{ ...styles.chipEntregaActivo, ...(falta > 0 ? styles.chipEntregaFalta : styles.chipEntregaCompleta) }}
                      title={falta > 0 ? `Faltan ${falta}` : "Entregado completo"}
                    >
                      {nombreSinCantidad(it.descripcion_producto)} · <strong>{entregada} / {aprobada}</strong>
                      {falta === 0 && " ✓"}
                    </span>
                  );
                })}
            </div>
            )}
            {activosEntregados.length > 8 && (
              <input
                type="text"
                value={busquedaActivosEntregados}
                onChange={(e) => setBusquedaActivosEntregados(e.target.value)}
                placeholder="🔍 Buscar por producto, Cod. Inventario, marca, serie, edificio o persona..."
                style={styles.avisarBuscador}
              />
            )}
            <div className="table-scroll" style={styles.activosEntregadosWrap}>
              <table style={{ ...styles.avisarTabla, minWidth: 980 }}>
                <thead>
                  <tr>
                    <th style={{ ...styles.avisarTh, width: 40, textAlign: "center" }}>#</th>
                    <th style={styles.avisarTh}>Producto</th>
                    <th style={styles.avisarTh}>Cod. Inventario</th>
                    <th style={styles.avisarTh}>Marca</th>
                    <th style={styles.avisarTh}>Modelo</th>
                    <th style={styles.avisarTh}>Serie</th>
                    <th style={styles.avisarTh}>Edificio</th>
                    <th style={styles.avisarTh}>{esStaff ? "Entregado a" : "Recibido por"}</th>
                    <th style={styles.avisarTh}>{esStaff ? "Fecha de entrega" : "Fecha de recepción"}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((a, i) => (
                    <tr key={a.id} style={{ ...styles.avisarFila, background: i % 2 === 1 ? "#f3f6fb" : "#fff" }}>
                      <td style={{ ...styles.avisarTd, textAlign: "center", color: "#8993a1" }}>{i + 1}</td>
                      <td style={{ ...styles.avisarTd, fontWeight: 600 }}>{nombreSinCantidad(a.descripcion_producto)}</td>
                      <td style={styles.avisarTd}>
                        <span style={styles.chipItem}>{a.codigo_activo || "—"}</span>
                      </td>
                      <td style={styles.avisarTd}>{a.marca || "—"}</td>
                      <td style={styles.avisarTd}>{a.modelo || "—"}</td>
                      <td style={styles.avisarTd}>{a.numero_serie || "—"}</td>
                      <td style={styles.avisarTd}>{a.edificio_nombre || "—"}</td>
                      <td style={styles.avisarTd}>{a.entregado_a || "—"}</td>
                      <td style={{ ...styles.avisarTd, whiteSpace: "nowrap" }}>
                        {a.entregado_en ? new Date(a.entregado_en).toLocaleDateString("es-NI") : "—"}
                      </td>
                    </tr>
                  ))}
                  {visibles.length === 0 && (
                    <tr>
                      <td colSpan={9} style={{ ...styles.avisarTd, textAlign: "center", color: "#8993a1" }}>
                        Ningún activo coincide con la búsqueda.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
  };

  // Productos normales que ya se entregaron (una fila por producto de cada entrega).
  const productosEntregados = (solicitud.entregas || []).flatMap((e) =>
    (e.items || []).map((ei) => ({ ...ei, numero_entrega: e.numero_entrega, recibido_por: e.recibido_por, entregado_en: e.creado_en }))
  );
  const activosEntregadosLista = (solicitud.items || []).flatMap((it) => (it.activos || []).filter((a) => a.entregado));

  // Lo que falta por entregar de cada producto aprobado (con entregas parciales).
  const entregaDe = (it) => {
    const aprobada = cantidadRelevante(it);
    const entregada = it.es_activo_fijo
      ? (it.activos || []).filter((a) => a.entregado).length
      : Number(it.cantidad_entregada_total || 0);
    return { aprobada, entregada, falta: Math.max(0, aprobada - entregada) };
  };
  const renderProductosEntregados = () => {
    if (productosEntregados.length === 0) return null;
    return (
      <div style={styles.activosEntregadosCard}>
        <h3 style={{ margin: "0 0 0.2rem", textAlign: "center" }}>
          ✅ {esStaff ? "Productos entregados" : "Productos recibidos"}{" "}
          <span style={styles.avisarContador}>{productosEntregados.length}</span>
        </h3>
        <div style={styles.barraDorada} />
        <Ayuda>
        <p style={{ margin: "0 0 0.8rem", fontSize: "0.8rem", color: "#8993a1", textAlign: "center" }}>
          {esStaff ? "Lo que se entregó en cada entrega y quién lo recibió." : "Lo que has recibido en cada entrega."}
        </p>
        </Ayuda>
        <div className="table-scroll" style={styles.activosEntregadosWrap}>
          <table style={{ ...styles.avisarTabla, minWidth: 640 }}>
            <thead>
              <tr>
                <th style={{ ...styles.avisarTh, width: 40, textAlign: "center" }}>#</th>
                <th style={styles.avisarTh}>Producto</th>
                <th style={{ ...styles.avisarTh, textAlign: "center" }}>{esStaff ? "Entregada / aprobada" : "Recibida / aprobada"}</th>
                <th style={styles.avisarTh}>Recibió</th>
                <th style={styles.avisarTh}>{esStaff ? "Fecha de entrega" : "Fecha de recepción"}</th>
              </tr>
            </thead>
            <tbody>
              {productosEntregados.map((p, i) => (
                <tr key={`${p.numero_entrega}-${p.solicitud_item_id}-${i}`} style={{ ...styles.avisarFila, background: i % 2 === 1 ? "#f3f6fb" : "#fff" }}>
                  <td style={{ ...styles.avisarTd, textAlign: "center", color: "#8993a1" }}>{i + 1}</td>
                  <td style={{ ...styles.avisarTd, fontWeight: 600 }}>{nombreSinCantidad(p.descripcion_producto)}</td>
                  <td style={{ ...styles.avisarTd, textAlign: "center", whiteSpace: "nowrap", fontWeight: 700 }}>
                    {(() => {
                      // Lo entregado hasta ahora de este producto, de lo aprobado: "10 / 12" = faltan 2.
                      const it = (solicitud.items || []).find((x) => x.id === p.solicitud_item_id);
                      const unidad = p.unidad ? ` ${p.unidad}` : "";
                      if (!it) return `${Number(p.cantidad_entregada)}${unidad}`;
                      const { aprobada, entregada, falta } = entregaDe(it);
                      return (
                        <span
                          style={{ color: falta > 0 ? "#b85a0b" : "#2f7a54" }}
                          title={falta > 0 ? `Faltan ${falta}${unidad}` : "Entregado completo"}
                        >
                          {entregada} / {aprobada}
                          {unidad}
                          {falta === 0 && " ✓"}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={styles.avisarTd}>{p.recibido_por || "—"}</td>
                  <td style={{ ...styles.avisarTd, whiteSpace: "nowrap" }}>
                    {p.entregado_en ? new Date(p.entregado_en).toLocaleDateString("es-NI") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Estado de pago de una cotización: pago de contado (tabla pagos) o factura a crédito.
  const pagoInfoDe = (c) => {
    const p = (solicitud.pagos || []).find((x) => x.cotizacion_id === c.id);
    if (p)
      return {
        existe: true,
        pagado: p.estado === "pagado",
        ruta: `/pagos/${p.id}/registrar`,
        tipo: "contado",
        id: p.id,
        docsPendientes: !p.docs_listos_en,
        datos: p,
      };
    const cr = (solicitud.pagos_credito || []).find((x) => x.cotizacion_id === c.id);
    if (cr)
      return {
        existe: true,
        pagado: cr.estado === "pagado",
        ruta: `/pagos/creditos/${cr.id}`,
        tipo: "credito",
        id: cr.id,
        docsPendientes: !cr.docs_listos_en,
        datos: cr,
      };
    return { existe: false, pagado: false, ruta: null };
  };

  // Estado propio de cada proveedor (cotización elegida) mientras la solicitud avanza: no todos van al mismo ritmo.
  // Devuelve null cuando conviene mostrar el estado general de la solicitud.
  // Quien gestiona la compra ve "Entregado" (lo que él entregó); el solicitante ve "Recibido".
  const mapaEstadosProveedor = esStaff
    ? { ...ESTADOS_SOLICITUD, entregada: { ...ESTADOS_SOLICITUD.entregada, texto: "Entregado" } }
    : ESTADOS_SOLICITUD;
  const estadoProveedorKey = (c) => {
    if (!["en_cola", "aprobada", "en_proceso_compra", "lista_para_retiro"].includes(solicitud.estado)) return null;
    const info = pagoInfoDe(c);
    const productos = c.items.map((ci) => (solicitud.items || []).find((it) => it.id === ci.id)).filter(Boolean);
    if (info.existe && info.docsPendientes && !info.pagado) return "aprobada";
    if (info.existe && info.tipo === "contado" && !info.pagado) return "en_proceso_pago";
    if (productos.length > 0 && productos.every((it) => itemEntregado(it))) return "entregada";
    if (productos.length > 0 && productos.every((it) => it.listo_retiro)) return "lista_para_retiro";
    if (info.existe && info.tipo === "contado" && info.pagado && solicitud.estado === "aprobada") return "pagada";
    return null;
  };

  const guardarDocumentosPago = async (form, enviar) => {
    setGuardandoDocs(true);
    setErrorDocs("");
    try {
      await apiFetch(`/api/pagos/documentos/${prepararPago.info.tipo}/${prepararPago.info.id}`, {
        method: "POST",
        body: JSON.stringify({ ...form, enviar }),
      });
      setPrepararPago(null);
      cargar();
    } catch (err) {
      setErrorDocs(err.message);
    } finally {
      setGuardandoDocs(false);
    }
  };
  const mostrarColumnaPago = esStaff && ((solicitud.pagos || []).length > 0 || (solicitud.pagos_credito || []).length > 0);

  // Con entregas registradas (parciales o totales) la pantalla pasa a "modo botones":
  // solo queda visible la tabla de cotizaciones y lo demás se abre con su botón.
  const tieneEntregas = productosEntregados.length > 0 || activosEntregadosLista.length > 0;
  const modoBotones = solicitudFinalizada || tieneEntregas;

  // Botones de la solicitud ya recibida: cada uno abre su sección debajo.
  // Orden: artículos solicitados → (productos / activos entregados) → reimprimir acta (a la derecha).
  const botonesFinal = [
    { key: "items", icono: "📦", texto: "Ver artículos solicitados", cuenta: (solicitud.items || []).length },
    ...(productosEntregados.length > 0
      ? [{ key: "productos", icono: "✅", texto: esStaff ? "Productos entregados" : "Productos recibidos", cuenta: productosEntregados.length }]
      : []),
    ...(activosEntregadosLista.length > 0
      ? [{ key: "activos", icono: "🏷️", texto: esStaff ? "Activos entregados" : "Activos recibidos", cuenta: activosEntregadosLista.length }]
      : []),
  ];

  return (
    <div>
      <button type="button" onClick={() => navigate("/bandeja")} style={styles.linkVolver}>
        ← Regresar a solicitudes
      </button>

      <EncabezadoDetalle
        titulo={`Solicitud #${String(solicitud.id).padStart(3, "0")}`}
        insignia={
          <>
            <Badge estado={estadoVisible(solicitud.estado, solicitud.total_cotizaciones, solicitud.tiene_pendiente_aprobacion, solicitud.en_atencion, solicitud.tiene_pago_pendiente, solicitud.tiene_pago_realizado)} mapa={mapaEstadosSolicitud()} />
            {(solicitud.items || []).some((it) => it.prioridad === "urgente") && (
              <span style={styles.badgeUrgente}>🔴 Urgente</span>
            )}
          </>
        }
      />

      <BarraProgreso
        estado={solicitud.estado}
        totalCotizaciones={solicitud.total_cotizaciones}
        tienePendienteAprobacion={solicitud.tiene_pendiente_aprobacion}
        enAtencion={solicitud.en_atencion}
        tienePagoPendiente={solicitud.tiene_pago_pendiente}
        tienePagoRealizado={solicitud.tiene_pago_realizado}
      />

      <div style={styles.infoCard}>
        <div style={styles.infoEncabezado}>
          <div className="form-seccion-titulo" style={{ margin: 0 }}>📋 Datos de la solicitud</div>
          <div style={styles.infoChips}>
            <span style={styles.infoChip}>
              📦 {(solicitud.items || []).length} artículo{(solicitud.items || []).length === 1 ? "" : "s"}
            </span>
          </div>
        </div>
        <div style={styles.infoPanel}>
        <div style={styles.infoItem}>
          <span style={styles.infoIcono}>👤</span>
          <div>
            <div style={styles.infoLabel}>Solicitante</div>
            <div style={styles.infoValor}>{solicitud.solicitante_nombre}</div>
          </div>
        </div>
        <div style={styles.infoItem}>
          <span style={styles.infoIcono}>🏢</span>
          <div>
            <div style={styles.infoLabel}>Área</div>
            <div style={styles.infoValor}>{solicitud.area_nombre}</div>
          </div>
        </div>
        <div style={styles.infoItem}>
          <span style={styles.infoIcono}>🏗️</span>
          <div>
            <div style={styles.infoLabel}>Edificio{solicitud.edificios?.length > 1 ? "s" : ""}</div>
            <div style={styles.infoValor}>
              {solicitud.edificios?.length > 0
                ? solicitud.edificios.map((ed) => ed.nombre).join(", ")
                : solicitud.edificio_nombre}
            </div>
          </div>
        </div>
        <div style={styles.infoItem}>
          <span style={styles.infoIcono}>📅</span>
          <div>
            <div style={styles.infoLabel}>Fecha de solicitud</div>
            <div style={styles.infoValor}>{new Date(solicitud.creado_en).toLocaleDateString("es-NI")}</div>
          </div>
        </div>
        {/* Quién aprobó: mismo formato que los demás datos. Con uno solo, va en la esquina derecha. */}
        {(solicitud.aprobadores || []).map((a) => (
          <div
            key={`aprobador-${a.id}`}
            className="info-aprobador"
            style={{ ...styles.infoItem, ...(solicitud.aprobadores.length === 1 ? styles.infoAprobadorEsquina : {}) }}
          >
            <span style={styles.infoIcono}>✅</span>
            <div style={{ minWidth: 0 }}>
              <div style={styles.infoLabel}>Aprobada por</div>
              <div style={styles.infoValor}>{a.nombre}</div>
              {a.aprobado_en && (
                <div style={styles.infoSecundario}>
                  {new Date(a.aprobado_en).toLocaleDateString("es-NI")} ·{" "}
                  {new Date(a.aprobado_en).toLocaleTimeString("es-NI", { hour: "2-digit", minute: "2-digit" })}
                </div>
              )}
            </div>
          </div>
        ))}
        <div style={styles.infoRequerimiento}>
          <span style={styles.infoIcono}>📝</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={styles.infoLabel}>Requerimiento</div>
            <div style={styles.infoValor}>{solicitud.descripcion_requerimiento}</div>
          </div>
        </div>
        </div>
      </div>

      {motivoRechazo && (
        <div style={{ ...styles.avisoComentario, borderLeftColor: "#a3402f", background: "#fbe9e6" }}>
          <strong>{solicitud.estado === "rechazada" ? "Motivo del rechazo:" : "Motivo del rechazo anterior:"}</strong>{" "}
          {motivoRechazo}
        </div>
      )}

      {error && <p style={{ color: "#a3402f" }}>{error}</p>}

      {modoBotones && (
        <>
          <div style={styles.barraBotonesFinal}>
            {botonesFinal.map((b) => (
              <button
                key={b.key}
                type="button"
                onClick={() => {
                  const abrir = panelFinal !== b.key;
                  setPanelFinal(abrir ? b.key : null);
                  if (abrir && b.key === "items") setMostrarItems(true);
                }}
                style={{ ...styles.botonPanelFinal, ...(panelFinal === b.key ? styles.botonPanelFinalActivo : {}) }}
              >
                <span style={{ fontSize: "1.05rem" }}>{b.icono}</span>
                <span>{b.texto}</span>
                {b.cuenta != null && <span style={styles.botonPanelFinalCuenta}>{b.cuenta}</span>}
              </button>
            ))}

            {actasParaReimprimir.length > 0 && (
              <div style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={() =>
                    actasParaReimprimir.length === 1
                      ? reimprimirActa(actasParaReimprimir[0].ruta)
                      : setMenuActasAbierto((v) => !v)
                  }
                  style={styles.botonMenuAcciones}
                >
                  🖨️ Reimprimir acta{actasParaReimprimir.length > 1 ? " ▾" : ""}
                </button>
                {menuActasAbierto && actasParaReimprimir.length > 1 && (
                  <>
                    <div style={styles.overlayMenu} onClick={() => setMenuActasAbierto(false)} />
                    <div className="dropdown-menu" style={styles.dropdownMenu}>
                      {actasParaReimprimir.map((a) => (
                        <button
                          key={a.key}
                          type="button"
                          onClick={() => {
                            setMenuActasAbierto(false);
                            reimprimirActa(a.ruta);
                          }}
                          style={styles.dropdownItem}
                        >
                          <span style={{ width: "1.3em", display: "inline-block" }}>🖨️</span>
                          <span>
                            {a.texto}
                            {a.detalle && (
                              <span style={{ display: "block", fontSize: "0.72rem", color: "#8993a1" }}>{a.detalle}</span>
                            )}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {panelFinal === "productos" && renderProductosEntregados()}
          {panelFinal === "activos" && renderActivosEntregados()}
        </>
      )}

      {!solicitudFinalizada && itemsAccion.length > 0 && (
        <div style={styles.barraAcciones}>
          <div style={{ position: "relative" }}>
            <button
              type="button"
              disabled={procesando}
              onClick={() => setMenuAccionesAbierto((v) => !v)}
              style={styles.botonMenuAcciones}
            >
              ⋮ Más acciones
            </button>
            {menuAccionesAbierto && (
              <>
                <div style={styles.overlayMenu} onClick={() => setMenuAccionesAbierto(false)} />
                <div className="dropdown-menu" style={styles.dropdownMenu}>
                  {itemsAccion.map((it) => (
                    <button
                      key={it.key}
                      type="button"
                      disabled={procesando}
                      onClick={() => {
                        setMenuAccionesAbierto(false);
                        it.onClick();
                      }}
                      style={styles.dropdownItem}
                    >
                      <span style={{ width: "1.3em", display: "inline-block" }}>{it.icono}</span>
                      {it.texto}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {esCompras &&
        ["en_proceso_compra", "lista_para_retiro"].includes(solicitud.estado) &&
        (() => {
          const total = solicitud.items.length;
          const avisados = solicitud.items.filter((it) => it.listo_retiro).length;
          return (
            avisados > 0 &&
            avisados < total && (
              <p style={{ fontSize: "0.8rem", color: "#b8722a", margin: "0 0 1rem" }}>
                ⏳ Ya notificaste {avisados} de {total} artículos. Puedes notificar el resto cuando llegue.
              </p>
            )
          );
        })()}

      {esStaff && esCompras && (solicitud.pagos || []).some((p) => p.estado === "pendiente") && (
        <Ayuda>
          <p style={{ fontSize: "0.82rem", color: "#b8722a", margin: "0 0 1rem" }}>
            ⏳ Hay pagos de contado pendientes: los productos a <strong>crédito</strong> ya puedes
            gestionarlos con el proveedor; los de contado esperan a que Pagos los registre (te llega el
            aviso por correo). Mira la columna <strong>Pago</strong> en la tabla de cotizaciones, abajo.
          </p>
        </Ayuda>
      )}

      {mostrarActaBienes && (solicitud.actas_bienes || []).length > 0 && (
        <div style={styles.panelValidacion}>
          <h3 style={{ marginTop: 0, marginBottom: 0, textAlign: "center" }}>🏷️ Actas de bienes etiquetados</h3>
          <div style={{ ...styles.barraDorada, marginBottom: "0.8rem" }} />
          <Ayuda>
            <p style={{ fontSize: "0.85rem", color: "#5b6472", margin: "0 0 0.8rem", textAlign: "center" }}>
              Cada acta se generó al registrar una entrega de activos, con la persona que de verdad recibió los
              bienes. Aquí solo se reimprime: no se puede cambiar a quién se entregó. El detalle de abajo es el mismo
              que lleva el PDF.
            </p>
          </Ayuda>

          {solicitud.actas_bienes.map((a) => (
            <div key={a.id} style={styles.actaBienesCard}>
              <div style={styles.actaBienesCabecera}>
                <div>
                  <div style={styles.actaBienesNumero}>ACTA-{String(a.numero_acta).padStart(4, "0")}</div>
                  <div style={styles.actaBienesSub}>
                    {a.creado_en ? new Date(a.creado_en).toLocaleString("es-NI") : "—"}
                  </div>
                </div>
                <div>
                  <div style={styles.actaBienesEtiqueta}>Recibió</div>
                  <strong>{a.recibido_por}</strong>
                  {a.recibido_correo && <div style={styles.actaBienesSub}>{a.recibido_correo}</div>}
                </div>
                <span style={styles.actaBienesChip}>
                  {a.unidades.length} activo{a.unidades.length === 1 ? "" : "s"}
                </span>
                <button
                  type="button"
                  onClick={async () => {
                    setError("");
                    try {
                      await abrirArchivoProtegido(`/api/solicitudes/${id}/acta-bienes?acta_id=${a.id}`);
                    } catch (err) {
                      setError(err.message);
                    }
                  }}
                  style={styles.actaBienesBoton}
                >
                  🖨️ Reimprimir PDF
                </button>
              </div>

              <div className="table-scroll" style={styles.actaBienesTablaWrap}>
                <table style={styles.actaBienesTabla}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.actaBienesTh, width: 36, textAlign: "center" }}>#</th>
                      <th style={styles.actaBienesTh}>Cod. Inventario</th>
                      <th style={styles.actaBienesTh}>Descripción</th>
                      <th style={styles.actaBienesTh}>Edificio</th>
                      <th style={styles.actaBienesTh}>Marca</th>
                      <th style={styles.actaBienesTh}>Modelo</th>
                      <th style={styles.actaBienesTh}>Serie</th>
                      <th style={styles.actaBienesTh}>Fecha adq.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.unidades.map((u, i) => (
                      <tr key={u.id} style={{ background: i % 2 === 1 ? "#f3f6fb" : "#fff", borderTop: "1px solid #eef1f3" }}>
                        <td style={{ ...styles.actaBienesTd, textAlign: "center", color: "#8993a1" }}>{i + 1}</td>
                        <td style={{ ...styles.actaBienesTd, fontWeight: 700 }}>{u.codigo_activo || "—"}</td>
                        <td style={styles.actaBienesTd}>{u.descripcion}</td>
                        <td style={styles.actaBienesTd}>{u.edificio_nombre || "—"}</td>
                        <td style={styles.actaBienesTd}>{u.marca || "—"}</td>
                        <td style={styles.actaBienesTd}>{u.modelo || "—"}</td>
                        <td style={styles.actaBienesTd}>{u.numero_serie || "—"}</td>
                        <td style={{ ...styles.actaBienesTd, whiteSpace: "nowrap" }}>
                          {u.fecha_adquisicion ? new Date(`${u.fecha_adquisicion}T00:00:00`).toLocaleDateString("es-NI") : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {mostrarActaBienes && (solicitud.actas_bienes || []).length === 0 && (
        <div style={styles.panelValidacion}>
          <h3 style={{ textAlign: "center" }}>Responsable del bien</h3>
          <div style={{ marginBottom: "0.75rem" }}>
            <BuscadorPersona personas={usuarios} valor={custodioId} onChange={setCustodioId} />
          </div>

          {(() => {
            const custodioSeleccionado = usuarios.find(
              (u) => String(u.id) === String(custodioId)
            );
            if (!custodioSeleccionado) return null;
            const esElMismoSolicitante = String(custodioId) === String(solicitud.solicitante_id);
            return (
              <div style={styles.resumenValidacion}>
                {esElMismoSolicitante && (
                  <p style={{ margin: "0 0 0.4rem", color: "#5b6472", fontStyle: "italic" }}>
                    Es el mismo solicitante — se completó automáticamente con los datos de su solicitud.
                  </p>
                )}
                <p style={{ margin: "0 0 0.2rem" }}>
                  <strong>Área:</strong> {custodioSeleccionado.area_nombre || solicitud.area_nombre || "—"}
                </p>
                <p style={{ margin: "0 0 0.2rem" }}>
                  <strong>Cargo:</strong> {custodioSeleccionado.cargo || "—"}
                </p>
                <p style={{ margin: 0 }}>
                  <strong>Edificio:</strong> {custodioSeleccionado.edificio_nombre || solicitud.edificio_nombre || "—"}
                </p>
              </div>
            );
          })()}

          <p style={{ fontSize: "0.85rem", fontWeight: 600, margin: "0.5rem 0" }}>
            ¿No está en la lista? Créala aquí:
          </p>
          <div className="form-grid" style={styles.itemGridCustodio}>
            <input
              type="text"
              placeholder="Nombre"
              value={nuevoCustodio.nombre}
              onChange={(e) => setNuevoCustodio((p) => ({ ...p, nombre: e.target.value }))}
              style={styles.input}
            />
            <input
              type="email"
              placeholder="Correo institucional"
              value={nuevoCustodio.correo}
              onChange={(e) => setNuevoCustodio((p) => ({ ...p, correo: e.target.value }))}
              style={styles.input}
            />
            <select
              value={nuevoCustodio.area_id}
              onChange={(e) => setNuevoCustodio((p) => ({ ...p, area_id: e.target.value }))}
              style={styles.input}
            >
              <option value="">Área...</option>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nombre}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Cargo"
              value={nuevoCustodio.cargo}
              onChange={(e) => setNuevoCustodio((p) => ({ ...p, cargo: e.target.value }))}
              style={styles.input}
            />
            <select
              value={nuevoCustodio.edificio_id}
              onChange={(e) => setNuevoCustodio((p) => ({ ...p, edificio_id: e.target.value }))}
              style={styles.input}
            >
              <option value="">Edificio...</option>
              {edificios.map((ed) => (
                <option key={ed.id} value={ed.id}>
                  {ed.nombre}
                </option>
              ))}
            </select>
          </div>
          <button type="button" disabled={creandoCustodio} onClick={crearCustodio} style={{ marginTop: "0.5rem" }}>
            {creandoCustodio ? "Creando..." : "➕ Crear y seleccionar"}
          </button>

          <div style={{ marginTop: "1rem" }}>
            <button type="button" onClick={generarActaBienes}>
              Generar PDF
            </button>
          </div>
        </div>
      )}

      {/* La decisión de aprobar/rechazar vive solo en la pantalla dedicada del
          aprobador (/pendientes) — mostrarla también acá era redundante y esa
          otra pantalla ya tiene cantidad parcial, IVA y demás. */}

      {!modoBotones && solicitud.entregas && solicitud.entregas.length > 0 && (
        <div style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ textAlign: "center" }}>Entregas registradas</h3>
          {solicitud.entregas.map((e) => (
            <div key={e.id} style={styles.tarjetaEntrega}>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
                <strong>Entrega #{e.numero_entrega}</strong>
                <span style={{ fontSize: "0.78rem", color: "#5b6472" }}>
                  {new Date(e.creado_en).toLocaleString("es-NI")} · Recibió: {e.recibido_por}
                </span>
              </div>

              <ul style={{ margin: "0.5rem 0", paddingLeft: "1.1rem", fontSize: "0.85rem" }}>
                {e.items.map((it) => (
                  <li key={it.id}>
                    {it.cantidad_entregada} {it.unidad} de {it.descripcion_producto}
                  </li>
                ))}
              </ul>

              {e.estado_confirmacion === "confirmada" && (
                <span style={{ ...styles.badgeYaAvisado, background: "#dcf0e6", color: "#2f7a54" }}>
                  ✓ Confirmada por el solicitante
                </span>
              )}
              {e.estado_confirmacion === "diferencia" && (
                <div>
                  <span style={{ ...styles.badgeYaAvisado, background: "#f7ded9", color: "#a3402f" }}>
                    ⚠ Diferencia reportada
                  </span>
                  <p style={{ fontSize: "0.82rem", margin: "0.3rem 0 0" }}>"{e.comentario_confirmacion}"</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className={esStaff ? "two-col-grid" : undefined} style={esStaff ? styles.layoutPrincipal : undefined}>
        <div style={esStaff ? styles.columnaPrincipal : undefined}>
          {(!modoBotones || panelFinal === "items") && (
          <div style={styles.seccionCard}>
            <div style={styles.seccionHeader}>
              <span style={styles.seccionIcono}>📦</span>
              <div style={{ textAlign: "center" }}>
                <h3 style={{ margin: 0 }}>Ítems solicitados</h3>
                <div style={styles.barraDorada} />
                <p style={{ fontSize: "0.82rem", color: "#5b6472", margin: "0.15rem 0 0" }}>
                  {solicitud.items.length} artículo{solicitud.items.length === 1 ? "" : "s"} en esta solicitud.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setMostrarItems((v) => !v)}
                style={{ ...styles.botonPequeno, justifySelf: "end", alignSelf: "center" }}
              >
                {mostrarItems ? "▲ Ocultar productos solicitados" : "▼ Mostrar productos solicitados"}
              </button>
            </div>

            {mostrarItems && (
              <>
            {esCompras && solicitud.estado === "en_proceso_compra" && (
              <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.85rem", marginBottom: "0.75rem" }}>
                <input
                  type="checkbox"
                  checked={solicitud.items
                    .filter((it) => !yaVaComoNormal(it))
                    .every((it) => (formsActivo[it.id] || formActivoVacio(it.cantidad)).es_activo_fijo)}
                  onChange={(e) => marcarTodosActivoFijo(e.target.checked)}
                />
                Marcar todos los artículos como activo fijo
              </label>
            )}

            {!esStaff ? (
              <div style={{ overflowX: "auto" }}>
                <table className="tabla-items-solicitante" style={styles.tabla}>
                  <thead>
                    <tr style={{ textAlign: "center", borderBottom: "1px solid #d3d9df" }}>
                      <th style={styles.th}>#</th>
                      <th style={styles.th}>Producto</th>
                      <th style={styles.th}>Cantidad solicitada</th>
                      <th style={styles.th}>Cantidad rechazada</th>
                      <th style={styles.th}>Cantidad aprobada</th>
                      <th style={styles.th}>Estado</th>
                      <th style={styles.th}>Proveedor</th>
                      <th style={styles.th}>Forma de pago</th>
                    </tr>
                  </thead>
                  <tbody>
                    {solicitud.items.map((item) => {
                      const estadoItemMostrado = estadoItemVisible(item, solicitud);
                      const icono =
                        estadoItemMostrado === "cotizando"
                          ? "🕓"
                          : ["validado", "aprobada", "pagada", "en_proceso_compra", "lista_para_retiro", "entregada"].includes(estadoItemMostrado)
                          ? "✅"
                          : "⏳";
                      // Mismas cantidades que ve el equipo: solicitada, rechazada y aprobada.
                      // Antes de que el gerente decida, rechazada y aprobada muestran "—".
                      const sinAprobar = Math.max(0, Number(item.cantidad) - Number(item.cantidad_aprobada || 0));
                      const decidido = !!item.descartado || Number(item.cantidad_aprobada) > 0 || !!item.aprobado;
                      const cantidadRechazada = item.descartado
                        ? Math.min(sinAprobar, Number(item.cantidad_descartada) || sinAprobar)
                        : Math.min(sinAprobar, Number(item.cantidad_descartada) || 0);
                      const unidadTxt = item.unidad ? ` ${item.unidad}` : "";
                      return (
                        <tr key={item.id} style={{ borderBottom: "1px solid #eef1f3", textAlign: "center" }}>
                          <td style={styles.td}>{item.numero_item}</td>
                          <td style={styles.td}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.6rem" }}>
                              {item.archivo_referencia && <MiniaturaArchivo nombreArchivo={item.archivo_referencia} />}
                              <span>{item.descripcion_producto}</span>
                            </div>
                          </td>
                          <td style={{ ...styles.td, whiteSpace: "nowrap" }}>
                            {item.cantidad}
                            {unidadTxt}
                          </td>
                          <td style={{ ...styles.td, whiteSpace: "nowrap" }}>
                            {decidido && cantidadRechazada > 0 ? (
                              <NotaEmergente titulo="Motivo del rechazo" texto={comentarioDeAprobacion}>
                                <span style={{ fontWeight: 700, color: "#a3402f" }}>
                                  {cantidadRechazada}
                                  {unidadTxt}
                                </span>
                              </NotaEmergente>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td style={{ ...styles.td, whiteSpace: "nowrap" }}>
                            {decidido ? (
                              <span style={{ fontWeight: 700, color: Number(item.cantidad_aprobada) > 0 ? "#2f7a54" : "#8993a1" }}>
                                {Number(item.cantidad_aprobada) || 0}
                                {unidadTxt}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td style={styles.td}>
                            <span style={{ marginRight: "0.35rem" }}>{icono}</span>
                            <Badge estado={estadoItemMostrado} mapa={mapaEstadosItem()} />
                          </td>
                          {/* Un producto comprado en partes a varios proveedores los muestra todos, con su cantidad. */}
                          <td style={styles.td}>
                            {(item.proveedores_elegidos || []).length > 1
                              ? proveedorDeItem(item)
                              : item.cotizacion_elegida?.proveedor_nombre || "—"}
                          </td>
                          <td style={styles.td}>
                            {item.cotizacion_elegida ? (
                              <BadgeFormaPago valor={item.cotizacion_elegida.forma_pago} />
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              renderListaItemsStaff()
            )}
              </>
            )}
          </div>
          )}

          {(solicitud.cotizaciones.length > 0 || (esCompras && solicitud.estado === "en_cola")) && (
            <div style={styles.seccionCard}>
              <div style={styles.seccionHeader}>
                <span style={styles.seccionIcono}>💰</span>
                <div style={{ textAlign: "center" }}>
                  <h3 style={{ margin: 0 }}>Cotizaciones de proveedores</h3>
                  <div style={styles.barraDorada} />
                  <Ayuda>
                    <p style={{ fontSize: "0.82rem", color: "#5b6472", margin: "0.15rem 0 0" }}>
                      Cada cotización es lo que manda un proveedor: un monto total que puede cubrir uno o
                      varios artículos de la solicitud a la vez.
                    </p>
                  </Ayuda>
                </div>
              </div>

              {proveedoresResumen.length > 0 && (
                <div style={styles.chipsProveedores}>
                  {proveedoresResumen.map((p) => {
                    const activo = proveedorVisto === p.nombre;
                    return (
                      <button
                        type="button"
                        key={p.nombre}
                        onClick={() => setProveedorVisto(activo ? null : p.nombre)}
                        aria-expanded={activo}
                        title={activo ? "Ocultar sus productos" : "Ver los productos que cotizó"}
                        style={{
                          ...styles.chipProveedor,
                          ...(p.seleccionada ? styles.chipProveedorSeleccionado : {}),
                          ...styles.chipProveedorBoton,
                          ...(decisionTomada && p.seleccionada ? styles.chipProveedorElegido : {}),
                          ...(decisionTomada && !p.seleccionada ? styles.chipProveedorNoElegido : {}),
                          ...(activo ? styles.chipProveedorActivo : {}),
                        }}
                      >
                        <span style={{ ...styles.chipAvatar, ...(p.seleccionada ? styles.chipAvatarSeleccionado : {}) }}>
                          {p.nombre.charAt(0).toUpperCase()}
                        </span>
                        <div style={{ textAlign: "left" }}>
                          <div style={{ fontWeight: 700, fontSize: "0.82rem" }}>{p.nombre}</div>
                          <div style={{ fontSize: "0.78rem", color: "#5b6472" }}>{textoPorMoneda(p.sumas)}</div>
                        </div>
                        {p.seleccionada ? (
                          <span style={{ ...styles.chipBadgeSeleccionada, ...(decisionTomada ? styles.badgeElegida : {}) }}>
                            {decisionTomada ? "✓ Elegida" : "Seleccionada"}
                          </span>
                        ) : (
                          decisionTomada && <span style={styles.badgeNoElegida}>No elegida</span>
                        )}
                        <span style={{ fontSize: "0.7rem", color: "#8993a1", marginLeft: "0.2rem" }}>{activo ? "▴" : "▾"}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {proveedorVisto && (() => {
                const cotsProveedor = (solicitud.cotizaciones || []).filter((c) => c.proveedor_nombre === proveedorVisto);
                if (cotsProveedor.length === 0) return null;
                const totalProductos = (solicitud.items || []).length;
                const lineas = cotsProveedor.flatMap((c) => [
                  ...c.items.map((ci) => ({ c, ci, it: (solicitud.items || []).find((x) => x.id === ci.id) })),
                  ...(c.items_descartados || []).map((ci) => ({
                    c,
                    ci,
                    it: (solicitud.items || []).find((x) => x.id === ci.id),
                    descartado: ci.motivo,
                  })),
                ]);
                const cubiertos = new Set(lineas.map((l) => l.ci.id)).size;
                const dinero = (c, monto) =>
                  `${c.moneda === "USD" ? "US$" : "C$"}${Number(monto || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                return (
                  <div id="panel-productos-proveedor" style={styles.panelProductosProveedor}>
                    <div style={styles.panelProductosEncabezado}>
                      <div className="form-seccion-titulo" style={{ margin: 0 }}>
                        Productos que cotizó {proveedorVisto}
                      </div>
                      <span
                        style={{
                          ...styles.chipCobertura,
                          ...(cubiertos === totalProductos ? styles.chipCoberturaTodos : styles.chipCoberturaParcial),
                        }}
                      >
                        {cubiertos === totalProductos ? `✓ Todos (${totalProductos})` : `Cotizó ${cubiertos} de ${totalProductos}`}
                      </span>
                      <button type="button" onClick={() => setProveedorVisto(null)} style={{ ...styles.botonPequeno, marginLeft: "auto" }}>
                        ✕ Cerrar
                      </button>
                    </div>
                    <div style={{ maxHeight: 340, overflowY: "auto" }}>
                      <table style={{ ...styles.tabla, fontSize: "0.8rem" }}>
                        <thead>
                          <tr>
                            <th style={{ ...styles.th, textAlign: "center", width: 50 }}>#</th>
                            <th style={{ ...styles.th, textAlign: "left" }}>Producto</th>
                            <th style={{ ...styles.th, textAlign: "center" }}>Cantidad</th>
                            <th style={{ ...styles.th, textAlign: "right" }}>Precio (sin IVA)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lineas.map(({ c, ci, it, descartado }, i) => (
                            <tr key={`${c.id}-${ci.id}-${descartado ? "d" : "a"}`} style={{ background: i % 2 === 1 ? "#f7f9fc" : "#fff" }}>
                              <td style={{ ...styles.td, textAlign: "center", color: "#8993a1" }}>{it?.numero_item ?? i + 1}</td>
                              <td style={{ ...styles.td, textAlign: "left" }}>
                                {it?.descripcion_producto || ci.descripcion_producto}
                                {descartado && (
                                  <span style={styles.chipNoElegido}>
                                    {descartado === "no_elegida" ? "Se compró a otro proveedor" : "Rechazado"}
                                  </span>
                                )}
                              </td>
                              <td style={{ ...styles.td, textAlign: "center", whiteSpace: "nowrap" }}>
                                {it ? `${it.cantidad}${it.unidad ? ` ${it.unidad}` : ""}` : "—"}
                              </td>
                              <td style={{ ...styles.td, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                                {dinero(c, ci.precio)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {esCompras && solicitud.estado === "en_cola" && (
                <div style={{ display: "flex", justifyContent: "flex-end", margin: "0.7rem 0 0.3rem" }}>
                  <Link
                    to={`/solicitud/${id}/cotizacion-asistida`}
                    style={{ ...styles.botonAgregarCotizacion, textDecoration: "none", display: "inline-block" }}
                    title="Sube la cotización del proveedor y el sistema lee los productos y los precios"
                  >
                    ✨ Carga automática de cotización
                  </Link>
                </div>
              )}
              {esCompras && solicitud.estado === "en_cola" && (
                <Ayuda>
                  <p style={{ fontSize: "0.82rem", color: "#5b6472", margin: "0 0 0.4rem", textAlign: "right" }}>
                    Sube la cotización de cada proveedor (PDF digital) con este botón. Repítelo para cargar todas las
                    cotizaciones que necesite la solicitud — hasta {parametro("max_cotizaciones_por_producto")} por producto.
                  </p>
                </Ayuda>
              )}

              {solicitud.cotizaciones.length === 0 ? (
                <p style={{ fontSize: "0.85rem", color: "#5b6472", marginTop: "0.75rem" }}>
                  Todavía no hay cotizaciones.
                </p>
              ) : (
                <div style={{ overflowX: "auto", ...styles.tablaCard }}>
                  {/* Ancho mínimo: en pantallas medianas la tabla se desplaza de lado
                      en vez de aplastarse (se cortaba "Forma de pago" y los botones). */}
                  <table className="tabla-tarjetas" style={{ ...styles.tabla, minWidth: 980 }}>
                    <thead>
                      <tr style={{ textAlign: "center", borderBottom: "1px solid #d3d9df" }}>
                        <th style={styles.th}>Solicitud</th>
                        <th style={styles.th}>Proveedor</th>
                        <th style={styles.th}>Productos de cotización</th>
                        <th style={styles.th}>Monto total</th>
                        <th style={styles.th}>Forma de pago</th>
                        <th style={styles.th}>Estado</th>
                        {mostrarColumnaPago && <th style={styles.th}>Pago</th>}
                        <th style={styles.th}>Archivo</th>
                        {esCompras && (
                          <th style={{ ...styles.th, ...styles.colAccionesSticky, background: "var(--tema-primario, #1f4e96)" }}></th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {cotizacionesOrdenadas.map(({ c, est }, indiceCotizacion) => {
                        // Se usa también en la celda de acciones (más abajo) para que, al
                        // quedar fija ("sticky") sobre el resto de la fila al hacer scroll,
                        // no se note un color distinto al de su propia fila.
                        // Las elegidas alternan dos tonos: con uno solo, varias seguidas se veían como un bloque.
                        const filaFondo = c.seleccionada
                          ? indiceCotizacion % 2 === 1
                            ? "#fbf3e8"
                            : "#f2e3d1"
                          : indiceCotizacion % 2 === 1
                            ? "#fafbfc"
                            : "#fff";
                        const columnasTabla = 7 + (mostrarColumnaPago ? 1 : 0) + (esCompras ? 1 : 0);
                        const primeraPendiente = !est.historial && indiceCotizacion === 0;
                        const primeraHistorial =
                          est.historial && (indiceCotizacion === 0 || !cotizacionesOrdenadas[indiceCotizacion - 1].est.historial);
                        const separador = (texto) => (
                          <tr>
                            <td
                              colSpan={columnasTabla}
                              style={{ ...styles.td, textAlign: "left", background: "var(--tema-suave, #eef3fa)", color: "var(--tema-titulo, #0b3c60)", fontWeight: 700, fontSize: "0.78rem" }}
                            >
                              {texto}
                            </td>
                          </tr>
                        );
                        return (
                        <React.Fragment key={c.id}>
                        {solicitud.estado === "en_cola" && hayHistorial && hayPendientes && primeraPendiente &&
                          separador("Cotizaciones para los productos que faltan por decidir")}
                        {solicitud.estado === "en_cola" && hayHistorial && hayPendientes && primeraHistorial &&
                          separador("Cotizaciones de productos ya aprobados (historial)")}
                        <tr
                          style={{
                            // Línea gruesa entre un proveedor y otro (la más ancha gana a la de las celdas).
                            borderTop: indiceCotizacion > 0 ? "3px solid #8fa3c2" : undefined,
                            background: filaFondo,
                            // Las no elegidas quedan a la vista pero en segundo plano.
                            ...(noElegida(c) ? { opacity: 0.6 } : {}),
                          }}
                        >
                          <td
                            className="solo-escritorio"
                            style={{
                              ...styles.td,
                              fontWeight: 600,
                              borderLeft: c.seleccionada ? "4px solid #b8722a" : "4px solid transparent",
                            }}
                          >
                            #{String(solicitud.id).padStart(3, "0")}
                          </td>
                          <td className="celda-titulo" style={styles.td}>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", justifyContent: "flex-start" }}>
                              <span style={{ ...styles.chipAvatar, ...(c.seleccionada ? styles.chipAvatarSeleccionado : {}) }}>
                                {c.proveedor_nombre.charAt(0).toUpperCase()}
                              </span>
                              <div style={{ textAlign: "left" }}>
                                <div>{c.proveedor_nombre}</div>
                                {!!c.seleccionada && decisionTomada && (
                                  <span style={{ ...styles.badgeElegida, display: "inline-block", marginTop: "0.2rem", marginRight: "0.3rem" }}>
                                    ✓ Elegida
                                  </span>
                                )}
                                {!!c.seleccionada && (
                                  <span style={{ ...styles.badgeRecomendada, display: "inline-block", marginTop: "0.2rem" }}>
                                    ★ Recomendada por compras
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          {/* Solo el resumen: la lista completa sale al tocar la tarjeta del proveedor (arriba). */}
                          <td data-label="Productos" style={styles.td}>
                            {(() => {
                              const totalProductos = (solicitud.items || []).length;
                              const cotizados = idsCotizadosDe(c).size;
                              const todos = cotizados >= totalProductos;
                              return (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setProveedorVisto(c.proveedor_nombre);
                                    setTimeout(() => document.getElementById("panel-productos-proveedor")?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
                                  }}
                                  title="Ver los productos de esta cotización"
                                  style={{
                                    ...styles.chipCobertura,
                                    ...(noElegida(c) ? styles.chipCoberturaNoElegida : todos ? styles.chipCoberturaTodos : styles.chipCoberturaParcial),
                                    cursor: "pointer",
                                    fontFamily: "inherit",
                                  }}
                                >
                                  {todos ? `✓ Todos (${totalProductos})` : `Cotizó ${cotizados} de ${totalProductos}`}
                                </button>
                              );
                            })()}
                          </td>
                          <td data-label="Monto" style={{ ...styles.td, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                            {c.seleccionada ? (
                              formatoMonto(c)
                            ) : (
                              <span style={{ color: "#5b6472" }} title="Lo que cotizó este proveedor">
                                {formatoMonto({ ...c, monto_total: montoMostrado(c) })}
                              </span>
                            )}
                          </td>
                          <td data-label="Forma de pago" style={styles.td}>
                            {/* A una no elegida no se le va a pagar: la forma de pago no aplica. */}
                            {noElegida(c) ? <span style={{ color: "#8993a1" }}>—</span> : <BadgeFormaPago valor={c.forma_pago} />}
                          </td>
                          <td data-label="Estado" style={styles.td}>
                            {/* Etapa del proceso (la misma que muestra la solicitud arriba).
                                Una cotización que no se eligió, una vez que la solicitud ya
                                avanzó, no sigue ese proceso. */}
                            {solicitud.estado === "en_cola" ? (
                              est.historial && c.seleccionada && estadoProveedorKey(c) ? (
                                <Badge estado={estadoProveedorKey(c)} mapa={mapaEstadosProveedor} />
                              ) : (
                                <span style={{ ...styles.badgeEntregado, background: est.bg, color: est.color }}>{est.texto}</span>
                              )
                            ) : c.seleccionada && estadoProveedorKey(c) ? (
                              // Estado propio de cada proveedor (aprobada → proceso de pago → pagada → lista para retiro).
                              <Badge estado={estadoProveedorKey(c)} mapa={mapaEstadosProveedor} />
                            ) : c.seleccionada || solicitud.estado === "nueva" ? (
                              <Badge
                                estado={estadoVisible(
                                  solicitud.estado,
                                  solicitud.total_cotizaciones,
                                  solicitud.tiene_pendiente_aprobacion,
                                  solicitud.en_atencion,
                                  solicitud.tiene_pago_pendiente,
                                  solicitud.tiene_pago_realizado
                                )}
                                mapa={mapaEstadosSolicitud()}
                              />
                            ) : (
                              <span style={{ ...styles.badgeEntregado, background: "#eef1f3", color: "#5b6472" }}>
                                No elegida
                              </span>
                            )}
                          </td>
                          {mostrarColumnaPago &&
                            (() => {
                              const info = pagoInfoDe(c);
                              if (!info.existe) {
                                return <td data-label="Pago" style={{ ...styles.td, color: "#8993a1", fontSize: "0.8rem" }}>—</td>;
                              }
                              return (
                                <td data-label="Pago" style={styles.td}>
                                  {info.pagado ? (
                                    <span style={{ ...styles.badgeEntregado, background: "#dcf0e6", color: "#2f7a54" }}>
                                      ✓ Pagado
                                    </span>
                                  ) : info.docsPendientes ? (
                                    <span style={{ ...styles.badgeEntregado, background: "#fff3cd", color: "#8a6d1a" }}>
                                      📋 Documentos por preparar
                                    </span>
                                  ) : (
                                    <span style={{ ...styles.badgeEntregado, background: "#fde6cf", color: "#b85a0b" }}>
                                      ⏳ Pendiente
                                    </span>
                                  )}
                                </td>
                              );
                            })()}
                          <td data-label="Archivo" style={styles.td}>
                            {c.archivo_cotizacion ? (
                              <a
                                href="#" onClick={(e) => abrirArchivoSeguro(e, c.archivo_cotizacion)}
                                style={styles.linkArchivo}
                              >
                                📄 Ver
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                          {esCompras && (
                            <td className="celda-accion" style={{ ...styles.td, ...styles.colAccionesSticky, background: filaFondo }}>
                              <div style={{ display: "flex", gap: "0.4rem", alignItems: "center", flexWrap: "wrap" }}>
                                {!c.seleccionada && solicitud.estado === "en_cola" && (
                                  <button onClick={() => seleccionarCotizacion(c.id)} style={styles.botonPequenoExito}>
                                    ✓ Elegir
                                  </button>
                                )}
                                <Link
                                  to={`/solicitud/${id}/cotizacion-asistida/${c.id}`}
                                  style={{ ...styles.botonPequeno, textDecoration: "none", display: "inline-block" }}
                                  title="Abrir el detalle completo de esta cotización"
                                >
                                  🧾 Detalle
                                </Link>
                                {(() => {
                                  const info = pagoInfoDe(c);
                                  return esCompras && info.existe && !info.pagado && info.docsPendientes ? (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setErrorDocs("");
                                        setPrepararPago({ c, info });
                                      }}
                                      style={{ ...styles.botonPequeno, background: "#fff3cd", borderColor: "#e5c76b", fontWeight: 700 }}
                                      title="Deja lista la documentación para que el pago aparezca en Pagos"
                                    >
                                      📋 Preparar pago
                                    </button>
                                  ) : null;
                                })()}
                                {(() => {
                                  const info = pagoInfoDe(c);
                                  return info.existe && info.pagado ? (
                                    <Link
                                      to={info.ruta}
                                      state={{ desde: `/solicitud/${id}` }}
                                      style={{ ...styles.botonPequeno, textDecoration: "none", display: "inline-block" }}
                                      title="Ver quién pagó, cuándo y el comprobante"
                                    >
                                      💳 Ver detalle de pago
                                    </Link>
                                  ) : null;
                                })()}
                                {solicitud.estado === "en_cola" && (
                                  <div style={{ position: "relative" }}>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        // Se abre con position:fixed, anclado a las coordenadas
                                        // reales del botón — esta columna vive dentro de una
                                        // tabla con scroll (overflowX:auto, que de paso recorta
                                        // también el eje vertical), así que un menú
                                        // position:absolute normal quedaba cortado por ese
                                        // recorte en vez de abrirse por encima de todo.
                                        const r = e.currentTarget.getBoundingClientRect();
                                        setMenuCotizacionPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
                                        setMenuCotizacionAbierto((v) => (v === c.id ? null : c.id));
                                      }}
                                      style={styles.botonIconoMenu}
                                      title="Más acciones"
                                    >
                                      ⋮
                                    </button>
                                    {menuCotizacionAbierto === c.id && menuCotizacionPos && createPortal(
                                      <>
                                        <div style={styles.overlayMenu} onClick={() => setMenuCotizacionAbierto(null)} />
                                        <div
                                          className="dropdown-menu"
                                          style={{
                                            ...styles.dropdownMenu,
                                            ...styles.dropdownMenuChico,
                                            position: "fixed",
                                            top: menuCotizacionPos.top,
                                            right: menuCotizacionPos.right,
                                            left: "auto",
                                          }}
                                        >
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setMenuCotizacionAbierto(null);
                                              eliminarCotizacion(c.id);
                                            }}
                                            style={{ ...styles.dropdownItem, color: "#a3402f" }}
                                          >
                                            <span style={{ width: "1.3em", display: "inline-block" }}>🗑️</span>
                                            Eliminar
                                          </button>
                                        </div>
                                      </>,
                                      document.body
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                        </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Con un solo aprobador basta el dato de arriba (cuadro "Datos de la solicitud"). */}
          {!solicitudFinalizada &&
            (solicitud.aprobadores || []).length > 1 &&
            (() => {
              const aprobadaCompleta = ["aprobada", "en_proceso_compra", "lista_para_retiro", "entregada"].includes(
                solicitud.estado
              );
              // Con un solo aprobador no se repite la lista de productos: las cantidades aprobadas ya están en la
              // tabla de ítems. Con varios, sí, para saber quién aprobó qué.
              const unSoloAprobador = solicitud.aprobadores.length === 1;
              const titulo = !esStaff
                ? unSoloAprobador
                  ? "Aprobada por:"
                  : "Te han aprobado los siguientes productos:"
                : aprobadaCompleta
                ? "Esta compra fue aprobada por:"
                : "Aprobado hasta ahora por:";
              return (
                <div style={styles.tarjetaAprobacion}>
                  <div style={styles.tituloAprobacion}>
                    <span style={styles.iconoAprobacion}>✅</span>
                    {titulo}
                  </div>

                  {solicitud.aprobadores.map((a) => (
                    <div key={a.id} style={styles.bloqueAprobador}>
                      <div style={styles.cabeceraAprobador}>
                        <span style={{ ...styles.chipAvatar, ...styles.chipAvatarSeleccionado }}>
                          {(a.nombre || "?").charAt(0).toUpperCase()}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={styles.etiquetaAprobador}>Aprobado por</div>
                          <div style={{ fontWeight: 700, color: "var(--tema-titulo, #0b3c60)" }}>{a.nombre}</div>
                          <div style={{ fontSize: "0.8rem", color: "#5b6472" }}>
                            {[a.cargo, a.area_nombre].filter(Boolean).join(" · ") || "—"}
                          </div>
                          <div style={{ fontSize: "0.78rem", color: "#8993a1" }}>✉ {a.correo}</div>
                        </div>
                        {a.aprobado_en && (
                          <div style={styles.fechaAprobacion}>
                            {new Date(a.aprobado_en).toLocaleDateString("es-NI")}
                            <br />
                            {new Date(a.aprobado_en).toLocaleTimeString("es-NI", { hour: "2-digit", minute: "2-digit" })}
                          </div>
                        )}
                      </div>
                      {!unSoloAprobador && (
                      <ul style={styles.listaProductosAprobados}>
                        {a.items.map((it, i) => (
                          <li key={i} style={styles.productoAprobado}>
                            <span style={styles.chipCantidad}>
                              {it.cantidad}
                              {it.unidad ? ` ${it.unidad}` : ""}
                            </span>
                            <span>{it.descripcion}</span>
                          </li>
                        ))}
                      </ul>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
        </div>

        {esStaff && (
          <div style={styles.columnaLateral}>
            <div style={styles.tarjetaLateral}>
              <h4 style={styles.tituloLateral}>Resumen de la solicitud</h4>
              <div style={styles.filaResumen}>
                <span>Ítems solicitados</span>
                <strong>{solicitud.items.length}</strong>
              </div>
              <div style={styles.filaResumen}>
                <span>Cotizaciones recibidas</span>
                <strong>{solicitud.cotizaciones.length}</strong>
              </div>
              <div style={styles.filaResumen}>
                <span>Proveedor seleccionado</span>
                <strong style={{ textAlign: "right" }}>{proveedorSeleccionadoTexto || "—"}</strong>
              </div>
              <div style={{ ...styles.filaResumen, borderTop: "1px solid #eef1f3", paddingTop: "0.5rem", marginTop: "0.3rem" }}>
                <span>Monto estimado</span>
                <strong style={{ color: "#2f7a54", fontSize: "1.05rem" }}>{textoPorMoneda(montoEstimado)}</strong>
              </div>
            </div>

            <Ayuda>
              <div style={styles.tarjetaConsejo}>
                <span style={{ fontSize: "1.2rem" }}>💡</span>
                <div>
                  <strong style={{ fontSize: "0.85rem" }}>Consejo</strong>
                  <p style={{ fontSize: "0.8rem", margin: "0.2rem 0 0" }}>
                    Compara las cotizaciones y selecciona la que mejor se adapte a la necesidad del
                    solicitante antes de enviar a aprobación.
                  </p>
                </div>
              </div>
            </Ayuda>

            <div style={styles.tarjetaLateral}>
              <h4 style={styles.tituloLateral}>Actividad reciente</h4>
              {historialVisible.length === 0 ? (
                <p style={{ fontSize: "0.8rem", color: "#5b6472" }}>Sin actividad registrada todavía.</p>
              ) : (
                historialVisible.map((h) => (
                  <div key={h.id} style={styles.filaActividad}>
                    <span style={styles.puntoActividad} />
                    <div>
                      <div style={{ fontSize: "0.82rem" }}>
                        Pasó a <strong>{mapaEstadosSolicitud()[h.estado_nuevo]?.texto || h.estado_nuevo}</strong>
                      </div>
                      <div style={{ fontSize: "0.72rem", color: "#8993a1" }}>
                        {new Date(h.creado_en).toLocaleString("es-NI")}
                        {h.usuario_nombre ? ` · ${h.usuario_nombre}` : ""}
                      </div>
                    </div>
                  </div>
                ))
              )}
              {historialDesc.length > 5 && (
                <button
                  type="button"
                  onClick={() => setHistorialAbierto((v) => !v)}
                  style={{ ...styles.botonPequeno, marginTop: "0.5rem" }}
                >
                  {historialAbierto ? "Ver menos" : "Ver historial completo"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {!modoBotones && renderActivosEntregados()}

      {modalRetiroAbierto && (() => {
        // Solo lo ya aprobado Y pagado (o a crédito con la documentación enviada): lo demás no se puede notificar todavía.
        const aprobadoPorGerencia = (it) => !!it.puede_notificar;
        // Y solo lo que todavía falta por notificar: un producto (o un proveedor completo) ya notificado no se vuelve a
        // ofrecer, ni como tarjeta ni como fila deshabilitada.
        const faltaNotificar = (it) =>
          it.es_activo_fijo
            ? (it.activos || []).length < cantidadRelevante(it) || (it.activos || []).some((a) => !a.avisado)
            : !(it.descartado && !(Number(it.cantidad_aprobada) > 0)) && cantidadRelevante(it) - it.cantidad_lista > 0;
        const todosActivo = solicitud.items.filter((it) => it.es_activo_fijo && aprobadoPorGerencia(it) && faltaNotificar(it));
        const todosNormales = solicitud.items.filter((it) => !it.es_activo_fijo && aprobadoPorGerencia(it) && faltaNotificar(it));
        // Con más de un proveedor, primero se elige el proveedor y solo se ven sus productos.
        const gruposProv = agruparPorProveedor([...todosNormales, ...todosActivo]);
        const variosProv = gruposProv.length > 1;
        const eligiendoProveedor = variosProv && proveedorAvisar === null;
        const itemsActivo = variosProv ? todosActivo.filter((it) => proveedorDeItem(it) === proveedorAvisar) : todosActivo;
        const itemsNormales = variosProv ? todosNormales.filter((it) => proveedorDeItem(it) === proveedorAvisar) : todosNormales;
        const filasActivos = itemsActivo.flatMap((it) => (it.activos || []).map((a) => ({ it, a })));
        const termino = busquedaAvisar.trim().toLowerCase();
        const coincide = (texto) => !termino || String(texto).toLowerCase().includes(termino);
        const normalesVisibles = itemsNormales.filter((it) => coincide(it.descripcion_producto));
        const activosVisibles = filasActivos.filter(({ it, a }) =>
          coincide([it.descripcion_producto, a.numero_unidad, a.codigo_activo, a.marca, a.modelo, a.numero_serie].join(" "))
        );
        const pendienteDe = (it) => cantidadRelevante(it) - it.cantidad_lista;
        const activoAvisable = ({ it, a }) => !a.avisado && !!a.codigo_activo && !it.pago_pendiente;
        const nProductosAvisar = Object.values(cantidadesAvisar).filter((c) => Number(c) > 0).length;
        const nUnidadesAvisar = unidadesAvisarSeleccionadas.length;
        const itemsActivoIncompletos = itemsActivo.filter(activoIncompleto);

        const avisarTodoLoPendiente = () => {
          const todo = {};
          itemsNormales.forEach((it) => {
            if (!it.pago_pendiente && pendienteDe(it) > 0) todo[it.id] = pendienteDe(it);
          });
          setCantidadesAvisar(todo);
        };
        const abrirProveedorAvisar = (proveedor, itemsDelProveedor) => {
          const cantidades = {};
          const unidades = [];
          itemsDelProveedor.forEach((it) => {
            if (it.pago_pendiente) return;
            if (it.es_activo_fijo) {
              (it.activos || []).forEach((a) => {
                if (!a.avisado && a.codigo_activo) unidades.push(a.id);
              });
            } else {
              const pendiente = cantidadRelevante(it) - it.cantidad_lista;
              const rechazado = it.descartado && !(Number(it.cantidad_aprobada) > 0);
              if (!rechazado && pendiente > 0) cantidades[it.id] = pendiente;
            }
          });
          setCantidadesAvisar(cantidades);
          setUnidadesAvisarSeleccionadas(unidades);
          setBusquedaAvisar("");
          setError("");
          setProveedorAvisar(proveedor);
        };
        const seleccionarTodasLasListas = () =>
          setUnidadesAvisarSeleccionadas(filasActivos.filter(activoAvisable).map(({ a }) => a.id));

        return (
          <div style={styles.modalFondo} onClick={() => setModalRetiroAbierto(false)}>
            <div style={styles.modalAvisarCaja} onClick={(e) => e.stopPropagation()}>
              <div style={styles.avisarEncabezado}>
                <div>
                  <h3 style={{ margin: 0, color: "var(--tema-titulo, #0b3c60)" }}>📦 Notificar que el producto está listo</h3>
                  <Ayuda>
                  <p style={{ margin: "0.2rem 0 0", fontSize: "0.82rem", color: "#5b6472" }}>
                    Elige qué llegó y cuándo puede venir a retirarlo el solicitante.
                  </p>
                  </Ayuda>
                </div>
                <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                  <span style={styles.avisarChip}>
                    {nProductosAvisar} producto{nProductosAvisar === 1 ? "" : "s"}
                  </span>
                  <span style={styles.avisarChip}>
                    {nUnidadesAvisar} unidad{nUnidadesAvisar === 1 ? "" : "es"} de activo
                  </span>
                </div>
              </div>

              <Ayuda>
                <p style={{ fontSize: "0.85rem", color: "#5b6472" }}>
                  Indica cuánto llegó de cada producto normal (puede ser parcial) y marca qué unidades de activo
                  fijo ya están listas, luego escribe el mensaje para el solicitante.
                </p>
              </Ayuda>

              {itemsNormales.length + filasActivos.length > 8 && (
                <input
                  type="text"
                  value={busquedaAvisar}
                  onChange={(e) => setBusquedaAvisar(e.target.value)}
                  placeholder="🔍 Buscar producto, Cod. Inventario, marca, modelo o serie..."
                  style={styles.avisarBuscador}
                />
              )}

              {eligiendoProveedor && (
                <div style={styles.avisarSeccion}>
                  <div style={styles.avisarSeccionTitulo}>
                    <span>🏢 ¿De qué proveedor llegó el producto?</span>
                  </div>
                  <p style={{ fontSize: "0.82rem", color: "#5b6472", margin: "0 0 0.6rem" }}>
                    Esta solicitud se compró a {contarProveedores([...todosNormales, ...todosActivo])} proveedores. Elige uno para ver sus productos y
                    notificar lo que ya llegó; después puedes hacer lo mismo con los demás.
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "0.7rem" }}>
                    {gruposProv.map(([proveedor, itemsDelProveedor]) => {
                      const porNotificar = itemsDelProveedor.reduce((suma, it) => {
                        if (it.pago_pendiente) return suma;
                        if (it.es_activo_fijo) return suma + (it.activos || []).filter((a) => !a.avisado && a.codigo_activo).length;
                        const rechazado = it.descartado && !(Number(it.cantidad_aprobada) > 0);
                        return suma + (!rechazado && cantidadRelevante(it) - it.cantidad_lista > 0 ? 1 : 0);
                      }, 0);
                      const esperandoPago = itemsDelProveedor.some((it) => it.pago_pendiente);
                      return (
                        <button
                          key={proveedor}
                          type="button"
                          onClick={() => abrirProveedorAvisar(proveedor, itemsDelProveedor)}
                          style={{
                            textAlign: "left",
                            background: "#fff",
                            border: "1px solid #c9d6ea",
                            borderRadius: 12,
                            padding: "0.8rem 1rem",
                            cursor: "pointer",
                            fontFamily: "inherit",
                            boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
                          }}
                        >
                          <div style={{ fontWeight: 700, color: "var(--tema-titulo, #0b3c60)", fontSize: "0.95rem" }}>🏢 {proveedor}</div>
                          <div style={{ fontSize: "0.8rem", color: "#5b6472", margin: "0.25rem 0 0.5rem" }}>
                            {itemsDelProveedor.length} producto{itemsDelProveedor.length === 1 ? "" : "s"}
                          </div>
                          {porNotificar > 0 ? (
                            <span style={{ ...styles.avisarEstado, background: "#d7e8f7", color: "#1d5fa8" }}>
                              {porNotificar} por notificar
                            </span>
                          ) : esperandoPago ? (
                            <span style={{ ...styles.avisarEstado, background: "var(--tema-titulo, #0b3c60)", color: "#fff" }}>⏳ Esperando pago</span>
                          ) : (
                            <span style={{ ...styles.avisarEstado, background: "#dcf0e6", color: "#2f7a54" }}>✓ Todo notificado</span>
                          )}
                          <span style={{ float: "right", color: "var(--tema-primario, #2f6690)", fontWeight: 600, fontSize: "0.8rem" }}>Ver productos →</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {variosProv && !eligiendoProveedor && (
                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", margin: "0 0 0.6rem" }}>
                  <button type="button" onClick={() => setProveedorAvisar(null)} style={styles.avisarBotonChico}>
                    ← Proveedores
                  </button>
                  <strong style={{ color: "var(--tema-titulo, #0b3c60)" }}>🏢 {proveedorAvisar}</strong>
                </div>
              )}

              {itemsNormales.length > 0 && (
                <div style={styles.avisarSeccion}>
                  <div style={styles.avisarSeccionTitulo}>
                    <span>
                      🛒 Productos <span style={styles.avisarContador}>{itemsNormales.length}</span>
                    </span>
                    <span style={{ display: "flex", gap: "0.4rem" }}>
                      <button type="button" onClick={avisarTodoLoPendiente} style={styles.avisarBotonChico}>
                        Notificar todo lo pendiente
                      </button>
                      <button type="button" onClick={() => setCantidadesAvisar({})} style={styles.avisarBotonChico}>
                        Quitar todo
                      </button>
                    </span>
                  </div>
                  {agruparPorProveedor(itemsNormales).length > 1 && (
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.4rem", margin: "0 0 0.5rem" }}>
                      <span style={{ fontSize: "0.78rem", color: "#5b6472", fontWeight: 600 }}>Notificar solo lo de:</span>
                      {agruparPorProveedor(itemsNormales).map(([proveedor, itemsDeProv]) => (
                        <button
                          key={proveedor}
                          type="button"
                          style={styles.avisarBotonChico}
                          title={`Deja marcado únicamente lo pendiente de ${proveedor} y quita lo demás`}
                          onClick={() => {
                            const solo = {};
                            itemsDeProv.forEach((it) => {
                              const p = pendienteDe(it);
                              const rechazado = it.descartado && !(Number(it.cantidad_aprobada) > 0);
                              if (!it.pago_pendiente && !rechazado && p > 0) solo[it.id] = p;
                            });
                            setCantidadesAvisar(solo);
                          }}
                        >
                          🏢 {proveedor}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="table-scroll" style={styles.avisarTablaWrap}>
                    <table style={styles.avisarTabla}>
                      <thead>
                        <tr>
                          <th style={{ ...styles.avisarTh, width: 44, textAlign: "center" }}>#</th>
                          <th style={styles.avisarTh}>Producto</th>
                          <th style={{ ...styles.avisarTh, textAlign: "center" }}>Cantidad</th>
                          <th style={{ ...styles.avisarTh, textAlign: "center" }}>Notificado</th>
                          <th style={{ ...styles.avisarTh, textAlign: "center" }}>Pendiente</th>
                          <th style={{ ...styles.avisarTh, textAlign: "center" }}>A notificar ahora</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agruparPorProveedor(normalesVisibles).map(([proveedor, itemsDeProv]) => (
                          <React.Fragment key={proveedor}>
                            <tr>
                              <td colSpan={6} style={{ ...styles.avisarTd, background: "var(--tema-suave, #eef3fa)", textAlign: "left" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                                  <strong style={{ color: "var(--tema-titulo, #0b3c60)" }}>
                                    🏢 {proveedor}{" "}
                                    <span style={{ fontWeight: 400, color: "#5b6472" }}>
                                      · {itemsDeProv.length} producto{itemsDeProv.length === 1 ? "" : "s"}
                                    </span>
                                  </strong>
                                  <button
                                    type="button"
                                    style={styles.avisarBotonChico}
                                    onClick={() => {
                                      const todo = { ...cantidadesAvisar };
                                      const avisables = itemsDeProv.filter(
                                        (it) =>
                                          !it.pago_pendiente &&
                                          !(it.descartado && !(Number(it.cantidad_aprobada) > 0)) &&
                                          pendienteDe(it) > 0
                                      );
                                      const yaTodoMarcado =
                                        avisables.length > 0 && avisables.every((it) => Number(todo[it.id]) === pendienteDe(it));
                                      avisables.forEach((it) => {
                                        if (yaTodoMarcado) delete todo[it.id];
                                        else todo[it.id] = pendienteDe(it);
                                      });
                                      setCantidadesAvisar(todo);
                                    }}
                                  >
                                    {itemsDeProv.length > 0 &&
                                    itemsDeProv.every(
                                      (it) =>
                                        it.pago_pendiente ||
                                        pendienteDe(it) <= 0 ||
                                        Number(cantidadesAvisar[it.id]) === pendienteDe(it)
                                    )
                                      ? "Quitar lo de este proveedor"
                                      : "Notificar todo de este proveedor"}
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {itemsDeProv.map((it) => {
                          const pendiente = pendienteDe(it);
                          const unidad = it.unidad ? ` ${it.unidad}` : "";
                          return (
                            <tr key={it.id} style={styles.avisarFila}>
                              <td style={{ ...styles.avisarTd, textAlign: "center", color: "#8993a1" }}>
                                {itemsNormales.indexOf(it) + 1}
                              </td>
                              <td style={{ ...styles.avisarTd, fontWeight: 600 }}>{it.descripcion_producto}</td>
                              <td style={{ ...styles.avisarTd, textAlign: "center", whiteSpace: "nowrap" }}>
                                {cantidadRelevante(it)}
                                {unidad}
                              </td>
                              <td style={{ ...styles.avisarTd, textAlign: "center", color: "#5b6472" }}>
                                {it.cantidad_lista || 0}
                              </td>
                              <td style={{ ...styles.avisarTd, textAlign: "center", fontWeight: 700 }}>
                                {Math.max(pendiente, 0)}
                              </td>
                              <td style={{ ...styles.avisarTd, textAlign: "center" }}>
                                {it.descartado && !(Number(it.cantidad_aprobada) > 0) ? (
                                  <span style={{ ...styles.avisarEstado, background: "#f7ded9", color: "#a3402f" }}>
                                    🚫 Rechazado
                                  </span>
                                ) : pendiente > 0 && it.pago_pendiente ? (
                                  <span style={{ ...styles.avisarEstado, background: "var(--tema-titulo, #0b3c60)", color: "#fff" }}>
                                    ⏳ Esperando pago
                                  </span>
                                ) : pendiente > 0 ? (
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}>
                                    <input
                                      type="number"
                                      min="0"
                                      max={pendiente}
                                      value={cantidadesAvisar[it.id] ?? 0}
                                      onChange={(e) => {
                                        const escrito = e.target.value;
                                        // Pasarse de lo pendiente se bloquea al instante: se deja el máximo
                                        // permitido y se avisa de inmediato (no hasta la otra ventana).
                                        if (Number(escrito) > pendiente) {
                                          setCantidadesAvisar((prev) => ({ ...prev, [it.id]: pendiente }));
                                          setError(
                                            `No puedes notificar más de lo pendiente en "${it.descripcion_producto}" (pendiente: ${pendiente}).`
                                          );
                                          return;
                                        }
                                        setCantidadesAvisar((prev) => ({ ...prev, [it.id]: escrito }));
                                        setError("");
                                      }}
                                      style={styles.avisarInputCantidad}
                                    />
                                    <button
                                      type="button"
                                      title="Notificar todo lo pendiente de este producto"
                                      onClick={() => setCantidadesAvisar((prev) => ({ ...prev, [it.id]: pendiente }))}
                                      style={styles.avisarBotonChico}
                                    >
                                      Todo
                                    </button>
                                  </span>
                                ) : (
                                  <span style={{ ...styles.avisarEstado, background: "#dcf0e6", color: "#2f7a54" }}>
                                    ✓ Notificado todo
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                            })}
                          </React.Fragment>
                        ))}
                        {normalesVisibles.length === 0 && (
                          <tr>
                            <td colSpan={6} style={{ ...styles.avisarTd, textAlign: "center", color: "#8993a1" }}>
                              Ningún producto coincide con la búsqueda.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {filasActivos.length > 0 && (
                <div style={styles.avisarSeccion}>
                  <div style={styles.avisarSeccionTitulo}>
                    <span>
                      🏷️ Activos fijos <span style={styles.avisarContador}>{filasActivos.length} unidades</span>
                    </span>
                    <span style={{ display: "flex", gap: "0.4rem" }}>
                      <button type="button" onClick={seleccionarTodasLasListas} style={styles.avisarBotonChico}>
                        Seleccionar todas las listas
                      </button>
                      <button type="button" onClick={() => setUnidadesAvisarSeleccionadas([])} style={styles.avisarBotonChico}>
                        Ninguna
                      </button>
                    </span>
                  </div>

                  {itemsActivoIncompletos.length > 0 && (
                    <div style={styles.avisarAlerta}>
                      ⚠ Faltan datos de unidades de:{" "}
                      {itemsActivoIncompletos.map((it, i) => (
                        <span key={it.id}>
                          {i > 0 ? ", " : ""}
                          <button
                            type="button"
                            onClick={() => {
                              setModalRetiroAbierto(false);
                              navigate(`/solicitud/${id}/activo/${it.id}`, {
                                state: { volverAAvisar: true, avisoRetiro: { fecha: fechaRetiro, hora: horaRetiro, mensaje: mensajeRetiro } },
                              });
                            }}
                            style={styles.avisarLink}
                          >
                            {it.descripcion_producto}
                          </button>
                        </span>
                      ))}
                      . Las unidades sin código no se pueden notificar.
                    </div>
                  )}

                  <div className="table-scroll" style={styles.avisarTablaWrap}>
                    <table style={{ ...styles.avisarTabla, minWidth: 900 }}>
                      <thead>
                        <tr>
                          <th style={{ ...styles.avisarTh, width: 44, textAlign: "center" }}>✓</th>
                          <th style={styles.avisarTh}>Producto</th>
                          <th style={{ ...styles.avisarTh, textAlign: "center" }}>Cantidad</th>
                          <th style={styles.avisarTh}>Cod. Inventario</th>
                          <th style={styles.avisarTh}>Marca</th>
                          <th style={styles.avisarTh}>Modelo</th>
                          <th style={styles.avisarTh}>Serie</th>
                          <th style={styles.avisarTh}>Edificio</th>
                          <th style={{ ...styles.avisarTh, textAlign: "center" }}>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activosVisibles.map(({ it, a }) => {
                          const bloqueado = a.avisado || !a.codigo_activo || it.pago_pendiente;
                          const estado = a.avisado
                            ? { texto: "✓ Notificado", bg: "#dcf0e6", color: "#2f7a54" }
                            : !a.codigo_activo
                            ? { texto: "Sin Cod. Inventario", bg: "#f7ded9", color: "#a3402f" }
                            : it.pago_pendiente
                            ? { texto: "⏳ Esperando pago", bg: "var(--tema-titulo, #0b3c60)", color: "#fff" }
                            : { texto: "Listo", bg: "#d7e8f7", color: "#1d5fa8" };
                          return (
                            <tr key={a.id} style={{ ...styles.avisarFila, opacity: bloqueado ? 0.7 : 1 }}>
                              <td style={{ ...styles.avisarTd, textAlign: "center" }}>
                                <input
                                  type="checkbox"
                                  checked={unidadesAvisarSeleccionadas.includes(a.id)}
                                  disabled={bloqueado}
                                  onChange={() => alternarUnidadAvisar(a.id)}
                                />
                              </td>
                              <td style={{ ...styles.avisarTd, fontWeight: 600 }}>{it.descripcion_producto}</td>
                              <td style={{ ...styles.avisarTd, textAlign: "center", fontWeight: 700 }}>1</td>
                              <td style={{ ...styles.avisarTd, fontWeight: 700 }}>{a.codigo_activo || "—"}</td>
                              <td style={styles.avisarTd}>{a.marca || "—"}</td>
                              <td style={styles.avisarTd}>{a.modelo || "—"}</td>
                              <td style={styles.avisarTd}>{a.numero_serie || "—"}</td>
                              <td style={styles.avisarTd}>{a.edificio_nombre || "—"}</td>
                              <td style={{ ...styles.avisarTd, textAlign: "center" }}>
                                <span style={{ ...styles.avisarEstado, background: estado.bg, color: estado.color }}>
                                  {estado.texto}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                        {activosVisibles.length === 0 && (
                          <tr>
                            <td colSpan={9} style={{ ...styles.avisarTd, textAlign: "center", color: "#8993a1" }}>
                              Ninguna unidad coincide con la búsqueda.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {eligiendoProveedor ? (
                <div style={styles.avisarPie}>
                  <span />
                  <button type="button" onClick={() => setModalRetiroAbierto(false)}>
                    Cancelar
                  </button>
                </div>
              ) : (
              <>
              <div style={styles.avisarFormulario}>
                <div>
                  <div style={styles.avisarEtiqueta}>¿Cuándo puede venir a retirar? *</div>
                  <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
                    <label style={styles.campoFechaRetiro}>
                      Día
                      <input
                        type="date"
                        value={fechaRetiro}
                        min={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => cambiarFechaHoraRetiro(e.target.value, horaRetiro)}
                        style={styles.input}
                      />
                    </label>
                    <label style={styles.campoFechaRetiro}>
                      Hora
                      <input
                        type="time"
                        value={horaRetiro}
                        onChange={(e) => cambiarFechaHoraRetiro(fechaRetiro, e.target.value)}
                        style={styles.input}
                      />
                    </label>
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 280 }}>
                  <div style={styles.avisarEtiqueta}>Mensaje para el solicitante</div>
                  <textarea
                    value={mensajeRetiro}
                    onChange={(e) => setMensajeRetiro(e.target.value)}
                    rows={3}
                    style={styles.textarea}
                  />
                </div>
              </div>

              {error && <p style={{ color: "#a3402f", margin: "0.6rem 0 0" }}>{error}</p>}

              <div style={styles.avisarPie}>
                <span style={{ fontSize: "0.82rem", color: "#5b6472" }}>
                  Se notificará: <strong>{nProductosAvisar}</strong> producto{nProductosAvisar === 1 ? "" : "s"} y{" "}
                  <strong>{nUnidadesAvisar}</strong> unidad{nUnidadesAvisar === 1 ? "" : "es"} de activo.
                </span>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button type="button" onClick={() => setModalRetiroAbierto(false)}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={enviandoRetiro}
                    onClick={enviarAvisoRetiro}
                    style={styles.botonPrimario}
                  >
                    {enviandoRetiro ? "Enviando..." : "✉️ Enviar notificación"}
                  </button>
                </div>
              </div>
              </>
              )}
            </div>
          </div>
        );
      })()}

      {exito && (
        <div role="status" style={styles.toastExito}>
          ✓ {exito}
        </div>
      )}

      {prepararPago && (
        <PrepararPagoModal
          titulo={prepararPago.info.tipo === "credito" ? "Preparar factura a crédito" : "Preparar pago"}
          proveedor={prepararPago.c.proveedor_nombre}
          monto={formatoMonto(prepararPago.c)}
          datos={prepararPago.info.datos}
          guardando={guardandoDocs}
          error={errorDocs}
          onGuardar={guardarDocumentosPago}
          onCerrar={() => setPrepararPago(null)}
        />
      )}

      {confirmandoNoActivos && (
        <ConfirmarNoActivoModal
          items={confirmandoNoActivos.items}
          procesando={enviandoRetiro}
          onCancelar={() => setConfirmandoNoActivos(null)}
          onMarcarActivos={(itemIds) => {
            if (!itemIds || itemIds.length === 0) return;
            const [primero, ...siguientes] = itemIds;
            setConfirmandoNoActivos(null);
            setModalRetiroAbierto(false);
            // volverAAvisar: al terminar de llenar los datos se regresa aquí y la ventana de
            // avisar retiro se abre sola. siguientesActivos: los demás marcados se abren después, en orden.
            // avisoRetiro: el día, la hora y el mensaje ya escritos, para que no se pierdan al volver.
            navigate(`/solicitud/${id}/activo/${primero}`, {
              state: {
                volverAAvisar: true,
                siguientesActivos: siguientes,
                proveedorAvisar,
                avisoRetiro: { fecha: fechaRetiro, hora: horaRetiro, mensaje: mensajeRetiro },
              },
            });
          }}
          onConfirmar={(itemsRestantes) =>
            enviarAvisoRetiroConfirmado(
              itemsRestantes.map((it) => ({ solicitud_item_id: it.id, cantidad: it.cantidad })),
              confirmandoNoActivos.activoIds
            )
          }
        />
      )}

      {modalEntregaAbierto && (() => {
        const disponibleDe = (it) => it.cantidad_lista - it.cantidad_entregada_total;
        const entregables = solicitud.items.filter((it) => !it.es_activo_fijo && disponibleDe(it) > 0);
        const termino = busquedaEntrega.trim().toLowerCase();
        const visibles = termino
          ? entregables.filter((it) => it.descripcion_producto.toLowerCase().includes(termino))
          : entregables;
        const nAEntregar = Object.values(cantidadesEntrega).filter((c) => Number(c) > 0).length;
        const entregarTodo = () => {
          const todo = {};
          entregables.forEach((it) => {
            todo[it.id] = disponibleDe(it);
          });
          setCantidadesEntrega(todo);
        };

        return (
          <div style={styles.modalFondo} onClick={() => setModalEntregaAbierto(false)}>
            <div style={{ ...styles.modalAvisarCaja, maxWidth: 900 }} onClick={(e) => e.stopPropagation()}>
              <div style={styles.avisarEncabezadoCentrado}>
                <span />
                <h3 style={{ margin: 0, color: "var(--tema-titulo, #0b3c60)", textAlign: "center" }}>📋 Registrar entrega</h3>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <span style={styles.avisarChip}>
                    {nAEntregar} de {entregables.length} producto{entregables.length === 1 ? "" : "s"} a entregar
                  </span>
                </div>
              </div>

              <Ayuda>
                <p style={{ fontSize: "0.85rem", color: "#5b6472", textAlign: "center", margin: "0 0 0.8rem" }}>
                  Indica cuánto se entrega hoy de cada artículo. Puedes entregar menos de lo pendiente si es una
                  entrega parcial — el resto queda pendiente para una próxima entrega. Solo se muestran los
                  artículos que ya notificaste como listos.
                </p>
              </Ayuda>

              <div style={styles.avisarEtiqueta}>¿Quién recibe el producto?</div>
              <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.85rem", marginBottom: "0.4rem" }}>
                <input
                  type="checkbox"
                  checked={recibeSolicitante}
                  onChange={(e) => alternarRecibeSolicitante(e.target.checked)}
                />
                Recibe el solicitante ({solicitud.solicitante_nombre})
              </label>
              {!recibeSolicitante && !recibeNuevo && (
                <>
                  <BuscadorPersona
                    personas={usuarios}
                    valor={recibidoPorId}
                    onChange={elegirQuienRecibe}
                    placeholder="Busca por nombre a quien recibe..."
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setRecibeNuevo(true);
                      setRecibidoPorId("");
                      setRecibidoPor("");
                    }}
                    style={styles.avisarLink}
                  >
                    ¿No está registrada? Escribir el nombre
                  </button>
                </>
              )}
              {!recibeSolicitante && recibeNuevo && (
                <>
                  <input
                    type="text"
                    value={recibidoPor}
                    onChange={(e) => setRecibidoPor(e.target.value)}
                    placeholder="Nombre de quien recibe"
                    style={styles.input}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setRecibeNuevo(false);
                      setRecibidoPor("");
                    }}
                    style={styles.avisarLink}
                  >
                    ← Buscar entre las personas registradas
                  </button>
                </>
              )}

              <div style={{ ...styles.avisarSeccion, marginTop: "1rem" }}>
                <div style={styles.avisarSeccionTitulo}>
                  <span>
                    🛒 Productos disponibles <span style={styles.avisarContador}>{entregables.length}</span>
                  </span>
                  <span style={{ display: "flex", gap: "0.4rem" }}>
                    <button type="button" onClick={entregarTodo} style={styles.avisarBotonChico}>
                      Entregar todo lo disponible
                    </button>
                    <button type="button" onClick={() => setCantidadesEntrega({})} style={styles.avisarBotonChico}>
                      Quitar todo
                    </button>
                  </span>
                </div>

                {entregables.length > 8 && (
                  <input
                    type="text"
                    value={busquedaEntrega}
                    onChange={(e) => setBusquedaEntrega(e.target.value)}
                    placeholder="🔍 Buscar producto..."
                    style={styles.avisarBuscador}
                  />
                )}

                <div className="table-scroll" style={{ ...styles.avisarTablaWrap, maxHeight: "40vh" }}>
                  <table style={styles.avisarTabla}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.avisarTh, width: 44, textAlign: "center" }}>#</th>
                        <th style={styles.avisarTh}>Producto</th>
                        <th style={{ ...styles.avisarTh, textAlign: "center" }}>Disponible</th>
                        <th style={{ ...styles.avisarTh, textAlign: "center" }}>A entregar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agruparPorProveedor(visibles).map(([proveedor, itemsDeProv]) => (
                        <React.Fragment key={proveedor}>
                          <tr>
                            <td colSpan={4} style={{ ...styles.avisarTd, background: "var(--tema-suave, #eef3fa)", textAlign: "left" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                                <strong style={{ color: "var(--tema-titulo, #0b3c60)" }}>
                                  🏢 {proveedor}{" "}
                                  <span style={{ fontWeight: 400, color: "#5b6472" }}>
                                    · {itemsDeProv.length} producto{itemsDeProv.length === 1 ? "" : "s"}
                                  </span>
                                </strong>
                                <button
                                  type="button"
                                  style={styles.avisarBotonChico}
                                  onClick={() => {
                                    const todo = { ...cantidadesEntrega };
                                    itemsDeProv.forEach((it) => {
                                      todo[it.id] = disponibleDe(it);
                                    });
                                    setCantidadesEntrega(todo);
                                  }}
                                >
                                  Entregar todo de este proveedor
                                </button>
                              </div>
                            </td>
                          </tr>
                          {itemsDeProv.map((it) => {
                        const disponible = disponibleDe(it);
                        return (
                          <tr key={it.id} style={styles.avisarFila}>
                            <td style={{ ...styles.avisarTd, textAlign: "center", color: "#8993a1" }}>
                              {entregables.indexOf(it) + 1}
                            </td>
                            <td style={{ ...styles.avisarTd, fontWeight: 600 }}>{it.descripcion_producto}</td>
                            <td style={{ ...styles.avisarTd, textAlign: "center", whiteSpace: "nowrap" }}>
                              {disponible}
                              {it.unidad ? ` ${it.unidad}` : ""}
                            </td>
                            <td style={{ ...styles.avisarTd, textAlign: "center" }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}>
                                <input
                                  type="number"
                                  min="0"
                                  max={disponible}
                                  value={cantidadesEntrega[it.id] ?? 0}
                                  onChange={(e) => {
                                    const escrito = e.target.value;
                                    if (Number(escrito) > disponible) {
                                      setCantidadesEntrega((prev) => ({ ...prev, [it.id]: disponible }));
                                      setError(
                                        `No puedes entregar más de lo disponible en "${it.descripcion_producto}" (disponible: ${disponible}).`
                                      );
                                      return;
                                    }
                                    setCantidadesEntrega((prev) => ({ ...prev, [it.id]: escrito }));
                                    setError("");
                                  }}
                                  style={styles.avisarInputCantidad}
                                />
                                <button
                                  type="button"
                                  title="Entregar todo lo disponible de este producto"
                                  onClick={() => setCantidadesEntrega((prev) => ({ ...prev, [it.id]: disponible }))}
                                  style={styles.avisarBotonChico}
                                >
                                  Todo
                                </button>
                              </span>
                            </td>
                          </tr>
                        );
                          })}
                        </React.Fragment>
                      ))}
                      {visibles.length === 0 && (
                        <tr>
                          <td colSpan={4} style={{ ...styles.avisarTd, textAlign: "center", color: "#8993a1" }}>
                            Ningún producto coincide con la búsqueda.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {error && <p style={{ color: "#a3402f", margin: "0.6rem 0 0" }}>{error}</p>}

              <div style={styles.avisarPie}>
                <span style={{ fontSize: "0.82rem", color: "#5b6472" }}>
                  Se entregará: <strong>{nAEntregar}</strong> producto{nAEntregar === 1 ? "" : "s"}.
                </span>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button type="button" onClick={() => setModalEntregaAbierto(false)}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={registrandoEntrega}
                    onClick={registrarEntrega}
                    style={styles.botonPrimario}
                  >
                    {registrandoEntrega ? "Guardando..." : "Registrar entrega y Generar Acta"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {modalEntregaActivosAbierto && (() => {
        const unidades = unidadesActivoEntregables();
        const termino = busquedaEntregaActivos.trim().toLowerCase();
        const visibles = termino
          ? unidades.filter((a) =>
              [a.descripcion_producto, a.numero_unidad, a.codigo_activo, a.marca, a.modelo, a.numero_serie, a.edificio_nombre]
                .join(" ")
                .toLowerCase()
                .includes(termino)
            )
          : unidades;
        const fechaCorta = (valor) => (valor ? String(valor).slice(0, 10).split("-").reverse().join("/") : "—");
        const todasMarcadas = unidades.length > 0 && activosSeleccionadosEntrega.length === unidades.length;

        return (
          <div style={styles.modalFondo} onClick={() => setModalEntregaActivosAbierto(false)}>
            <div style={styles.modalAvisarCaja} onClick={(e) => e.stopPropagation()}>
              <div style={styles.avisarEncabezadoCentrado}>
                <span />
                <h3 style={{ margin: 0, color: "var(--tema-titulo, #0b3c60)", textAlign: "center" }}>📋 Registrar entrega de activos</h3>
                <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <span style={styles.avisarChip}>
                    {activosSeleccionadosEntrega.length} de {unidades.length} unidad
                    {unidades.length === 1 ? "" : "es"} a entregar
                  </span>
                </div>
              </div>

              <Ayuda>
                <p style={{ fontSize: "0.85rem", color: "#5b6472", textAlign: "center", margin: "0 0 0.8rem" }}>
                  Se genera el Acta de Recepción de Bienes Etiquetados con las unidades que marques. Esta entrega es
                  distinta a la de productos normales y necesita un responsable (custodio) que reciba las unidades.
                </p>
              </Ayuda>

              <div style={styles.avisarSeccion}>
                <div style={styles.avisarSeccionTitulo}>
                  <span>
                    🏷️ Unidades a entregar <span style={styles.avisarContador}>{unidades.length}</span>
                  </span>
                  <span style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={() => setActivosSeleccionadosEntrega(unidades.map((a) => a.id))}
                      style={styles.avisarBotonChico}
                    >
                      Seleccionar todas
                    </button>
                    <button type="button" onClick={() => setActivosSeleccionadosEntrega([])} style={styles.avisarBotonChico}>
                      Ninguna
                    </button>
                  </span>
                </div>

                {unidades.length > 8 && (
                  <input
                    type="text"
                    value={busquedaEntregaActivos}
                    onChange={(e) => setBusquedaEntregaActivos(e.target.value)}
                    placeholder="🔍 Buscar por producto, Cod. Inventario, marca, modelo, serie o edificio..."
                    style={styles.avisarBuscador}
                  />
                )}

                <div className="table-scroll" style={{ ...styles.avisarTablaWrap, maxHeight: "40vh" }}>
                  <table style={{ ...styles.avisarTabla, minWidth: 960 }}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.avisarTh, width: 44, textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={todasMarcadas}
                            onChange={(e) =>
                              setActivosSeleccionadosEntrega(e.target.checked ? unidades.map((a) => a.id) : [])
                            }
                            title="Marcar o desmarcar todas"
                          />
                        </th>
                        <th style={styles.avisarTh}>Producto</th>
                        <th style={{ ...styles.avisarTh, textAlign: "center" }}>Cantidad</th>
                        <th style={styles.avisarTh}>Cod. Inventario</th>
                        <th style={styles.avisarTh}>Marca</th>
                        <th style={styles.avisarTh}>Modelo</th>
                        <th style={styles.avisarTh}>Serie</th>
                        <th style={styles.avisarTh}>Fecha de adquisición</th>
                        <th style={styles.avisarTh}>Edificio de destino</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibles.map((a) => (
                        <tr key={a.id} style={styles.avisarFila}>
                          <td style={{ ...styles.avisarTd, textAlign: "center" }}>
                            <input
                              type="checkbox"
                              checked={activosSeleccionadosEntrega.includes(a.id)}
                              onChange={() => alternarActivoEntrega(a.id)}
                            />
                          </td>
                          <td style={{ ...styles.avisarTd, fontWeight: 600 }}>{a.descripcion_producto}</td>
                          <td style={{ ...styles.avisarTd, textAlign: "center", fontWeight: 700 }}>1</td>
                          <td style={{ ...styles.avisarTd, fontWeight: 700 }}>{a.codigo_activo || "—"}</td>
                          <td style={styles.avisarTd}>{a.marca || "—"}</td>
                          <td style={styles.avisarTd}>{a.modelo || "—"}</td>
                          <td style={styles.avisarTd}>{a.numero_serie || "—"}</td>
                          <td style={{ ...styles.avisarTd, whiteSpace: "nowrap" }}>{fechaCorta(a.fecha_adquisicion)}</td>
                          <td style={styles.avisarTd}>{a.edificio_nombre || "—"}</td>
                        </tr>
                      ))}
                      {visibles.length === 0 && (
                        <tr>
                          <td colSpan={9} style={{ ...styles.avisarTd, textAlign: "center", color: "#8993a1" }}>
                            {unidades.length === 0
                              ? "No hay unidades avisadas pendientes de entregar."
                              : "Ninguna unidad coincide con la búsqueda."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={styles.avisarEtiqueta}>Responsable que recibe (custodio)</div>
              <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.85rem", marginBottom: "0.4rem" }}>
                <input
                  type="checkbox"
                  checked={custodioEsSolicitante}
                  onChange={(e) => alternarCustodioEsSolicitante(e.target.checked)}
                />
                Recibe el solicitante ({solicitud.solicitante_nombre})
              </label>
              {!custodioEsSolicitante && (
                <>
                  <BuscadorPersona personas={usuarios} valor={custodioId} onChange={setCustodioId} />

                  <p style={{ fontSize: "0.82rem", fontWeight: 600, margin: "0.6rem 0 0.3rem" }}>
                    ¿No está en la lista? Créala aquí:
                  </p>
                  <div className="form-grid" style={styles.itemGridCustodio}>
                    <input
                      type="text"
                      placeholder="Nombre"
                      value={nuevoCustodio.nombre}
                      onChange={(e) => setNuevoCustodio((p) => ({ ...p, nombre: e.target.value }))}
                      style={styles.input}
                    />
                    <input
                      type="email"
                      placeholder="Correo institucional"
                      value={nuevoCustodio.correo}
                      onChange={(e) => setNuevoCustodio((p) => ({ ...p, correo: e.target.value }))}
                      style={styles.input}
                    />
                    <select
                      value={nuevoCustodio.area_id}
                      onChange={(e) => setNuevoCustodio((p) => ({ ...p, area_id: e.target.value }))}
                      style={styles.input}
                    >
                      <option value="">Área...</option>
                      {areas.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.nombre}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      placeholder="Cargo"
                      value={nuevoCustodio.cargo}
                      onChange={(e) => setNuevoCustodio((p) => ({ ...p, cargo: e.target.value }))}
                      style={styles.input}
                    />
                    <select
                      value={nuevoCustodio.edificio_id}
                      onChange={(e) => setNuevoCustodio((p) => ({ ...p, edificio_id: e.target.value }))}
                      style={styles.input}
                    >
                      <option value="">Edificio...</option>
                      {edificios.map((ed) => (
                        <option key={ed.id} value={ed.id}>
                          {ed.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    disabled={creandoCustodio}
                    onClick={crearCustodio}
                    style={{ marginTop: "0.5rem" }}
                  >
                    {creandoCustodio ? "Creando..." : "➕ Crear y seleccionar"}
                  </button>
                </>
              )}

              {error && <p style={{ color: "#a3402f", margin: "0.6rem 0 0" }}>{error}</p>}

              <div style={styles.avisarPie}>
                <span style={{ fontSize: "0.82rem", color: "#5b6472" }}>
                  Se entregará: <strong>{activosSeleccionadosEntrega.length}</strong> unidad
                  {activosSeleccionadosEntrega.length === 1 ? "" : "es"} de activo.
                </span>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button type="button" onClick={() => setModalEntregaActivosAbierto(false)}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={registrandoEntregaActivos}
                    onClick={registrarEntregaActivos}
                    style={styles.botonPrimario}
                  >
                    {registrandoEntregaActivos ? "Guardando..." : "Registrar entrega y Generar Acta de Bienes"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

const styles = {
  toastExito: {
    position: "fixed",
    bottom: "1.5rem",
    right: "1.5rem",
    zIndex: 3000,
    background: "#2f7a54",
    color: "#fff",
    padding: "0.7rem 1.1rem",
    borderRadius: 10,
    fontSize: "0.88rem",
    fontWeight: 600,
    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
    maxWidth: 420,
  },
  linkVolver: {
    background: "none",
    border: "none",
    color: "var(--tema-primario, #2f6690)",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
    padding: 0,
    marginBottom: "0.75rem",
  },
  // Mismo diseño que "Datos de la cotización": línea de color, título con acento
  // y los datos sobre un fondo suave.
  infoEncabezado: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "0.5rem 1rem",
    marginBottom: "0.75rem",
  },
  infoChips: { display: "flex", flexWrap: "wrap", gap: "0.35rem" },
  infoChip: {
    fontSize: "0.74rem",
    fontWeight: 600,
    color: "#3d4a5c",
    background: "#f4f6f9",
    border: "1px solid #e2e6ea",
    borderRadius: 999,
    padding: "0.18rem 0.6rem",
    whiteSpace: "nowrap",
  },
  // Fila que se acomoda sola: los datos desde la izquierda y "Aprobada por" al extremo derecho.
  infoPanel: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: "0.9rem 2.2rem",
    background: "var(--tema-suave, #f5f8fc)",
    border: "1px solid var(--tema-linea, #e2e8f0)",
    borderRadius: 10,
    padding: "0.85rem 1rem",
  },
  // Empujado al extremo derecho de su fila.
  infoAprobadorEsquina: { marginLeft: "auto" },
  chipsEntregaActivos: { display: "flex", flexWrap: "wrap", gap: "0.4rem", justifyContent: "center", margin: "0.2rem 0 0.8rem" },
  chipEntregaActivo: { fontSize: "0.8rem", borderRadius: 999, padding: "0.25rem 0.75rem", border: "1px solid transparent" },
  chipEntregaFalta: { background: "#fdebd2", borderColor: "#f0d0a0", color: "#8a4f12" },
  chipEntregaCompleta: { background: "#dcf0e6", borderColor: "#bfe3cd", color: "#2f7a54" },
  infoSecundario: { fontSize: "0.74rem", color: "#8993a1", marginTop: "0.1rem" },
  infoRequerimiento: {
    flexBasis: "100%",
    display: "flex",
    alignItems: "flex-start",
    gap: "0.5rem",
    paddingTop: "0.75rem",
    borderTop: "1px dashed var(--tema-linea, #d5deec)",
  },
  infoCard: {
    border: "1px solid #d3d9df",
    borderTop: "3px solid var(--tema-primario, #1f4e8c)",
    borderRadius: 12,
    padding: "0.9rem 1.2rem 1.1rem",
    marginTop: "0.75rem",
    marginBottom: "0.75rem",
    background: "#fff",
    boxShadow: "0 1px 4px rgba(27,42,74,0.05)",
  },
  infoItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.5rem",
    minWidth: 140,
  },
  infoIcono: { fontSize: "1.1rem", flexShrink: 0 },
  infoLabel: { fontSize: "0.68rem", color: "#8993a1", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.02em" },
  infoValor: { fontSize: "0.88rem", color: "var(--tema-titulo, #0b3c60)", fontWeight: 600, wordBreak: "break-word" },
  avisoComentario: {
    background: "#f2e3d1",
    borderLeft: "3px solid #b8722a",
    borderRadius: "0 8px 8px 0",
    padding: "0.7rem 1rem",
    fontSize: "0.85rem",
    marginBottom: "1rem",
  },
  avisoAlertaRoja: {
    background: "#f7ded9",
    borderLeft: "3px solid #a3402f",
    borderRadius: "0 8px 8px 0",
    padding: "0.7rem 1rem",
    fontSize: "0.85rem",
    color: "#a3402f",
    marginBottom: "0.6rem",
  },
  barraAcciones: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
    marginBottom: "1rem",
  },
  botonAccionPrimario: {
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0.55rem 1rem",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  botonMenuAcciones: {
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0.55rem 1.1rem",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  botonIconoMenu: {
    background: "#fff",
    color: "#5b6472",
    border: "1px solid #d3d9df",
    borderRadius: 6,
    padding: "0.3rem 0.55rem",
    fontSize: "0.9rem",
    fontWeight: 700,
    cursor: "pointer",
    lineHeight: 1,
  },
  overlayMenu: {
    position: "fixed",
    inset: 0,
    zIndex: 20,
  },
  dropdownMenu: {
    position: "absolute",
    top: "calc(100% + 0.3rem)",
    left: 0,
    background: "#fff",
    border: "1px solid #e2e6ea",
    borderRadius: 10,
    boxShadow: "0 6px 20px rgba(27,42,74,0.16)",
    padding: "0.35rem",
    minWidth: 240,
    zIndex: 21,
    display: "flex",
    flexDirection: "column",
    gap: "0.1rem",
  },
  dropdownMenuChico: {
    minWidth: 150,
    right: 0,
    left: "auto",
  },
  dropdownItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    background: "transparent",
    border: "none",
    borderRadius: 6,
    padding: "0.5rem 0.6rem",
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "var(--tema-titulo, #0b3c60)",
    textAlign: "left",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  botonAgregarCotizacion: {
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "0.4rem 0.75rem",
    fontSize: "0.8rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  botonAccionSecundario: {
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0.55rem 1rem",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  botonAccionOutline: {
    background: "#fff",
    color: "var(--tema-primario, #2f6690)",
    border: "1px solid var(--tema-primario, #2f6690)",
    borderRadius: 8,
    padding: "0.55rem 1rem",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  botonAccionPeligro: {
    background: "#a3402f",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0.55rem 1rem",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  botonPequeno: {
    background: "#fff",
    color: "var(--tema-titulo, #0b3c60)",
    border: "1px solid #d3d9df",
    borderRadius: 999,
    padding: "0.3rem 0.65rem",
    fontSize: "0.74rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  botonPequenoAmbar: {
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    border: "1px solid var(--tema-titulo, #0b3c60)",
    borderRadius: 999,
    padding: "0.3rem 0.65rem",
    fontSize: "0.74rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  botonPequenoExito: {
    background: "#2f7a54",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "0.3rem 0.6rem",
    fontSize: "0.78rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  botonPequenoPeligro: {
    background: "#fff",
    color: "#a3402f",
    border: "1px solid #a3402f",
    borderRadius: 6,
    padding: "0.3rem 0.6rem",
    fontSize: "0.78rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  botonIconoPeligro: {
    background: "#fff",
    border: "1px solid #f7ded9",
    borderRadius: 6,
    padding: "0.3rem 0.5rem",
    fontSize: "0.85rem",
    cursor: "pointer",
    lineHeight: 1,
  },
  linkArchivo: {
    color: "var(--tema-primario, #2f6690)",
    fontWeight: 600,
    fontSize: "0.85rem",
    textDecoration: "none",
  },
  badgeRecomendada: {
    color: "#b8722a",
    fontWeight: 700,
    fontSize: "0.78rem",
  },
  seccionCard: {
    border: "1px solid #d3d9df",
    borderRadius: 12,
    padding: "1.1rem 1.2rem",
    marginTop: "1.5rem",
    background: "#fff",
    boxShadow: "0 1px 4px rgba(27,42,74,0.05)",
  },
  seccionHeader: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    gap: "0.6rem",
    marginBottom: "0.75rem",
  },
  seccionIcono: {
    fontSize: "1.3rem",
    background: "#eef1f3",
    borderRadius: 8,
    width: 34,
    height: 34,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  chipsProveedores: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.6rem",
    marginBottom: "0.9rem",
  },
  chipProveedor: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    border: "1px solid #d3d9df",
    borderRadius: 10,
    padding: "0.5rem 0.7rem",
    minWidth: 150,
  },
  chipProveedorSeleccionado: {
    border: "1px solid var(--tema-primario, #2f6690)",
    background: "#f4f8fb",
  },
  chipProveedorBoton: {
    background: "#fff",
    cursor: "pointer",
    fontFamily: "inherit",
    color: "inherit",
    transition: "box-shadow 0.15s, border-color 0.15s",
  },
  chipProveedorActivo: {
    border: "2px solid var(--tema-primario, #1f4e8c)",
    boxShadow: "0 0 0 3px var(--tema-suave, #e3ecf8)",
    background: "var(--tema-suave, #f4f8fb)",
  },
  panelProductosProveedor: {
    marginTop: "0.75rem",
    border: "1px solid var(--tema-linea, #d5deec)",
    borderTop: "3px solid var(--tema-primario, #1f4e8c)",
    borderRadius: 10,
    padding: "0.75rem 0.9rem",
    background: "#fff",
  },
  panelProductosEncabezado: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.5rem 0.8rem",
    marginBottom: "0.6rem",
  },
  chipCobertura: {
    fontSize: "0.74rem",
    fontWeight: 700,
    borderRadius: 999,
    padding: "0.2rem 0.65rem",
    border: "1px solid transparent",
    whiteSpace: "nowrap",
  },
  chipProveedorElegido: { border: "2px solid #2f7a54", background: "#eef8f2" },
  chipProveedorNoElegido: { opacity: 0.6, background: "#f6f7f9" },
  badgeElegida: {
    fontSize: "0.68rem",
    fontWeight: 700,
    color: "#fff",
    background: "#2f7a54",
    borderRadius: 999,
    padding: "0.1rem 0.5rem",
    whiteSpace: "nowrap",
  },
  badgeNoElegida: {
    fontSize: "0.68rem",
    fontWeight: 600,
    color: "#5b6472",
    background: "#e6e9ee",
    borderRadius: 999,
    padding: "0.1rem 0.5rem",
    whiteSpace: "nowrap",
  },
  chipCoberturaNoElegida: { background: "#eef1f3", borderColor: "#dfe3e8", color: "#5b6472" },
  chipNoElegido: {
    display: "inline-block",
    marginLeft: "0.4rem",
    fontSize: "0.68rem",
    fontWeight: 600,
    color: "#5b6472",
    background: "#eef1f3",
    borderRadius: 999,
    padding: "0.05rem 0.5rem",
  },
  chipCoberturaTodos: { background: "#e6f4ec", borderColor: "#c4e3d1", color: "#2f7a54" },
  // Cotizar solo una parte es normal (la solicitud se reparte entre proveedores): sin aspecto de alerta.
  chipCoberturaParcial: { background: "#e8f0fa", borderColor: "#c9daf0", color: "#1d4f8c" },
  chipAvatar: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    background: "#8993a1",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "0.85rem",
    flexShrink: 0,
  },
  chipAvatarSeleccionado: { background: "#2f7a54" },
  chipBadgeSeleccionada: {
    fontSize: "0.66rem",
    color: "var(--tema-primario, #2f6690)",
    background: "var(--tema-suave, #dceaf5)",
    borderRadius: 999,
    padding: "0.1rem 0.45rem",
    marginLeft: "auto",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  barraTabs: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.5rem",
    marginBottom: "0.9rem",
    paddingBottom: "0.75rem",
    borderBottom: "1px solid #eef1f3",
  },
  tab: {
    background: "#eef1f3",
    color: "#5b6472",
    border: "none",
    borderRadius: 999,
    padding: "0.3rem 0.65rem",
    fontSize: "0.74rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  tabActiva: {
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    border: "none",
    borderRadius: 999,
    padding: "0.3rem 0.65rem",
    fontSize: "0.74rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  layoutPrincipal: {
    display: "grid",
    gridTemplateColumns: "1fr 260px",
    gap: "1.25rem",
    alignItems: "start",
  },
  columnaPrincipal: { minWidth: 0 },
  columnaLateral: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    marginTop: "1.5rem",
    minWidth: 240,
  },
  tarjetaLateral: {
    border: "1px solid #d3d9df",
    borderRadius: 12,
    padding: "1rem",
    background: "#fff",
    boxShadow: "0 1px 4px rgba(27,42,74,0.05)",
  },
  tituloLateral: { margin: "0 0 0.6rem", fontSize: "0.9rem" },
  filaResumen: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.5rem",
    fontSize: "0.82rem",
    color: "#5b6472",
    padding: "0.3rem 0",
  },
  tarjetaConsejo: {
    display: "flex",
    gap: "0.5rem",
    border: "1px solid var(--tema-titulo, #0b3c60)",
    background: "var(--tema-titulo, #0b3c60)",
    borderRadius: 12,
    padding: "0.85rem",
    color: "#fff",
  },
  filaActividad: {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.5rem",
    padding: "0.4rem 0",
  },
  puntoActividad: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "var(--tema-primario, #2f6690)",
    marginTop: "0.4rem",
    flexShrink: 0,
  },
  modalFondo: {
    position: "fixed",
    inset: 0,
    background: "rgba(27,42,74,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "1rem",
  },
  modalCaja: {
    background: "#fff",
    borderRadius: 12,
    padding: "1.5rem",
    maxWidth: 480,
    width: "100%",
    boxShadow: "0 8px 30px rgba(27,42,74,0.25)",
    maxHeight: "85vh",
    overflowY: "auto",
  },
  // Panel de actas de bienes: una tarjeta por acta con el mismo detalle que el PDF.
  actaBienesCard: { background: "#fff", border: "1px solid #e3cba4", borderRadius: 12, marginBottom: "0.9rem", overflow: "hidden" },
  actaBienesCabecera: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1.2rem",
    flexWrap: "wrap",
    padding: "0.8rem 1rem",
    borderBottom: "1px solid #eef1f3",
    background: "#fafbfc",
    fontSize: "0.88rem",
    color: "var(--tema-titulo, #0b3c60)",
  },
  actaBienesNumero: { fontSize: "1.05rem", fontWeight: 800, color: "var(--tema-titulo, #0b3c60)" },
  actaBienesSub: { fontSize: "0.76rem", color: "#8993a1" },
  actaBienesEtiqueta: { fontSize: "0.68rem", fontWeight: 700, color: "#8993a1", textTransform: "uppercase", letterSpacing: "0.02em" },
  actaBienesChip: { background: "#d7e8f7", color: "#1d5fa8", borderRadius: 999, padding: "0.2rem 0.75rem", fontSize: "0.78rem", fontWeight: 700, whiteSpace: "nowrap" },
  actaBienesBoton: { background: "var(--tema-titulo, #0b3c60)", color: "#fff", border: "none", borderRadius: 8, padding: "0.5rem 0.95rem", fontSize: "0.84rem", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" },
  actaBienesTablaWrap: { overflow: "auto", maxHeight: "45vh" },
  actaBienesTabla: { width: "100%", minWidth: 820, borderCollapse: "collapse", fontSize: "0.85rem", color: "var(--tema-titulo, #0b3c60)" },
  actaBienesTh: { padding: "0.55rem 0.7rem", background: "var(--tema-primario, #1f4e96)", color: "#ffffff", fontSize: "0.72rem", fontWeight: 700, textAlign: "left", whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 1, textTransform: "uppercase", letterSpacing: "0.03em", border: "1px solid #ffffff" },
  actaBienesTd: { padding: "0.5rem 0.7rem", verticalAlign: "middle", border: "1px solid var(--tema-linea, #d5deec)" },
  // Tabla "Activos entregados" (al final de la solicitud).
  activosEntregadosCard: { background: "#fff", border: "1px solid #e2e6ea", borderRadius: 14, padding: "1.1rem 1.2rem", marginBottom: "1.5rem", boxShadow: "0 1px 3px rgba(27,42,74,0.06)" },
  activosEntregadosWrap: { border: "1px solid #e2e6ea", borderRadius: 10, maxHeight: "50vh", overflow: "auto" },
  // Ventana de "Avisar retiro": ancha y con tablas con scroll propio, para que
  // se vea bien igual con 2 productos que con 50.
  modalAvisarCaja: {
    background: "#fff",
    borderRadius: 14,
    padding: "1.4rem 1.6rem 1.2rem",
    width: "100%",
    maxWidth: 1280,
    maxHeight: "96vh",
    overflowY: "auto",
    boxShadow: "0 12px 40px rgba(27,42,74,0.3)",
  },
  avisarEncabezado: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "1rem",
    flexWrap: "wrap",
    paddingBottom: "0.9rem",
    marginBottom: "0.9rem",
    borderBottom: "1px solid #eef1f3",
  },
  // Encabezado con el título al centro (el aviso de cuántas unidades, a la derecha).
  avisarEncabezadoCentrado: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    gap: "1rem",
    paddingBottom: "0.9rem",
    marginBottom: "0.9rem",
    borderBottom: "1px solid #eef1f3",
  },
  avisarChip: {
    background: "#eef1f3",
    color: "var(--tema-titulo, #0b3c60)",
    borderRadius: 999,
    padding: "0.25rem 0.8rem",
    fontSize: "0.78rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  avisarBuscador: {
    width: "100%",
    boxSizing: "border-box",
    padding: "0.5rem 0.75rem",
    border: "1px solid #d3d9df",
    borderRadius: 8,
    fontFamily: "inherit",
    fontSize: "0.86rem",
    marginBottom: "0.8rem",
  },
  avisarSeccion: { marginBottom: "1rem" },
  avisarSeccionTitulo: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.6rem",
    flexWrap: "wrap",
    marginBottom: "0.5rem",
    fontSize: "0.92rem",
    fontWeight: 700,
    color: "var(--tema-titulo, #0b3c60)",
  },
  avisarContador: { marginLeft: "0.3rem", fontSize: "0.74rem", fontWeight: 700, color: "#5b6472", background: "#eef1f3", borderRadius: 999, padding: "0.1rem 0.55rem" },
  avisarBotonChico: {
    background: "#fff",
    color: "var(--tema-titulo, #0b3c60)",
    border: "1px solid #d3d9df",
    borderRadius: 999,
    padding: "0.22rem 0.7rem",
    fontSize: "0.74rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  avisarAlerta: {
    background: "#fdf3e3",
    color: "#7a4a17",
    borderRadius: 8,
    padding: "0.5rem 0.75rem",
    fontSize: "0.82rem",
    marginBottom: "0.5rem",
    lineHeight: 1.5,
  },
  avisarLink: { background: "none", border: "none", padding: 0, color: "var(--tema-primario, #2f6690)", fontWeight: 700, textDecoration: "underline", cursor: "pointer", fontSize: "inherit", fontFamily: "inherit" },
  avisarTablaWrap: { border: "1px solid #e2e6ea", borderRadius: 10, maxHeight: "56vh", overflow: "auto" },
  avisarTabla: { width: "100%", borderCollapse: "collapse", fontSize: "0.85rem", color: "var(--tema-titulo, #0b3c60)" },
  avisarTh: {
    padding: "0.55rem 0.7rem",
    background: "var(--tema-primario, #1f4e96)",
    color: "#ffffff",
    fontSize: "0.72rem",
    fontWeight: 700,
    textAlign: "left",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 1,
    border: "1px solid #ffffff",
  },
  avisarFila: { borderTop: "1px solid var(--tema-linea, #d5deec)" },
  avisarTd: { padding: "0.38rem 0.7rem", verticalAlign: "middle", border: "1px solid var(--tema-linea, #d5deec)" },
  avisarEstado: { display: "inline-block", borderRadius: 999, padding: "0.15rem 0.65rem", fontSize: "0.74rem", fontWeight: 700, whiteSpace: "nowrap" },
  avisarInputCantidad: { width: 70, border: "1px solid #d3d9df", borderRadius: 6, padding: "0.3rem 0.4rem", fontSize: "0.85rem", textAlign: "center", fontFamily: "inherit" },
  avisarFormulario: { display: "flex", gap: "1.2rem", flexWrap: "wrap", alignItems: "flex-start", marginTop: "0.4rem" },
  avisarEtiqueta: { fontSize: "0.85rem", fontWeight: 600, margin: "0 0 0.3rem", color: "var(--tema-titulo, #0b3c60)" },
  avisarPie: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "1rem",
    flexWrap: "wrap",
    marginTop: "1rem",
    paddingTop: "0.9rem",
    borderTop: "1px solid #eef1f3",
  },
  modalItems: {
    border: "1px solid #d3d9df",
    borderRadius: 8,
    padding: "0.5rem 0.75rem",
    maxHeight: 180,
    overflowY: "auto",
  },
  modalItemFila: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.4rem 0",
    borderBottom: "1px solid #eef1f3",
    fontSize: "0.85rem",
  },
  badgeYaAvisado: {
    marginLeft: "auto",
    fontSize: "0.72rem",
    color: "#2f7a54",
    background: "#dcf0e6",
    borderRadius: 999,
    padding: "0.1rem 0.5rem",
    whiteSpace: "nowrap",
  },
  tituloAprobacion: {
    display: "flex",
    alignItems: "center",
    gap: "0.55rem",
    fontWeight: 700,
    fontSize: "1rem",
    color: "#2f7a54",
    marginBottom: "0.8rem",
  },
  iconoAprobacion: {
    width: 30,
    height: 30,
    borderRadius: 8,
    background: "#dcf0e6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1rem",
  },
  bloqueAprobador: {
    background: "#fff",
    border: "1px solid #dcefe4",
    borderRadius: 10,
    padding: "0.75rem 0.9rem",
    marginBottom: "0.6rem",
  },
  cabeceraAprobador: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    paddingBottom: "0.6rem",
    borderBottom: "1px solid #eef5f0",
  },
  etiquetaAprobador: {
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "#2f7a54",
  },
  fechaAprobacion: { fontSize: "0.75rem", color: "#5b6472", textAlign: "right", lineHeight: 1.35, flexShrink: 0 },
  listaProductosAprobados: { listStyle: "none", margin: "0.5rem 0 0", padding: 0 },
  productoAprobado: {
    display: "flex",
    alignItems: "baseline",
    gap: "0.6rem",
    padding: "0.3rem 0",
    fontSize: "0.88rem",
    color: "var(--tema-titulo, #0b3c60)",
  },
  chipCantidad: {
    minWidth: 72,
    fontSize: "0.78rem",
    fontWeight: 700,
    color: "var(--tema-primario, #2f6690)",
    background: "var(--tema-suave, #eef4fb)",
    borderRadius: 999,
    padding: "0.1rem 0.55rem",
    textAlign: "center",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  tarjetaAprobacion: {
    background: "#f1faf5",
    border: "1px solid #cfe9db",
    borderRadius: 12,
    padding: "0.9rem 1.1rem",
    marginTop: "1.1rem",
  },
  filaAprobador: {
    display: "flex",
    alignItems: "center",
    gap: "0.7rem",
    padding: "0.4rem 0",
  },
  panelPagos: {
    background: "#f2fafb",
    border: "1px solid #cfe8ec",
    borderRadius: 10,
    padding: "0.8rem 1rem",
    margin: "0 0 1rem",
    fontSize: "0.85rem",
  },
  filaPago: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    flexWrap: "wrap",
    padding: "0.35rem 0",
    borderTop: "1px solid #dcedf0",
  },
  campoFechaRetiro: {
    display: "flex",
    flexDirection: "column",
    gap: "0.2rem",
    fontSize: "0.8rem",
    fontWeight: 600,
  },
  textarea: {
    width: "100%",
    padding: "0.6rem",
    border: "1px solid #d3d9df",
    borderRadius: 8,
    fontFamily: "inherit",
    fontSize: "0.9rem",
    resize: "vertical",
  },
  botonPrimario: {
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0.5rem 1rem",
    cursor: "pointer",
    fontWeight: 600,
  },
  filaEntregaItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "0.5rem 0",
    borderBottom: "1px solid #eef1f3",
  },
  tarjetaEntrega: {
    border: "1px solid #d3d9df",
    borderRadius: 10,
    padding: "0.9rem 1rem",
    marginBottom: "0.75rem",
    background: "#fff",
    boxShadow: "0 1px 4px rgba(27,42,74,0.06)",
  },
  panelValidacion: {
    border: "1px solid #b8722a",
    borderRadius: 8,
    padding: "1rem",
    marginBottom: "1.5rem",
    background: "#f2e3d1",
  },
  filaItemAprobar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.75rem",
    background: "#fff",
    border: "1px solid #e3cba4",
    borderRadius: 6,
    padding: "0.45rem 0.7rem",
    marginBottom: "0.4rem",
  },
  resumenValidacion: {
    background: "#fff",
    border: "1px solid #e3cba4",
    borderRadius: 6,
    padding: "0.6rem 0.8rem",
    fontSize: "0.85rem",
    marginBottom: "0.75rem",
  },
  itemCard: {
    borderBottom: "1px solid #eef1f3",
    padding: "0.85rem 0 0.85rem 0.85rem",
  },
  checkCotizacionItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.4rem",
    fontSize: "0.76rem",
    color: "var(--tema-titulo, #0b3c60)",
    background: "#eef1f6",
    border: "1px solid #d3d9df",
    borderRadius: 999,
    padding: "0.2rem 0.6rem",
    marginBottom: "0.5rem",
  },
  badgeEntregado: {
    fontSize: "0.72rem",
    color: "#2f7a54",
    background: "#dcf0e6",
    borderRadius: 999,
    padding: "0.15rem 0.55rem",
    whiteSpace: "nowrap",
    fontWeight: 600,
  },
  badgeParcial: {
    fontSize: "0.72rem",
    color: "#9c7a1a",
    background: "#f3ecd0",
    borderRadius: 999,
    padding: "0.15rem 0.55rem",
    whiteSpace: "nowrap",
    fontWeight: 600,
  },
  badgeUrgente: {
    fontSize: "0.72rem",
    color: "#a3402f",
    background: "#f7ded9",
    borderRadius: 999,
    padding: "0.15rem 0.55rem",
    whiteSpace: "nowrap",
    fontWeight: 700,
  },
  avisoDescartado: {
    fontSize: "0.8rem",
    color: "#a3402f",
    background: "#f7ded9",
    borderRadius: 8,
    padding: "0.5rem 0.7rem",
    margin: "0 0 0.6rem",
  },
  filaItemGrupo: {
    borderTop: "1px solid #eef1f3",
    marginTop: "0.75rem",
    paddingTop: "0.75rem",
  },
  itemHeader: {
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "0.5rem",
    marginBottom: "0.5rem",
  },
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" },
  // La columna de acciones (Elegir/Editar/Eliminar) queda fija al borde
  // derecho del área con scroll horizontal — si la tabla es más ancha que
  // la pantalla, esta columna nunca se pierde fuera de vista.
  colAccionesSticky: {
    position: "sticky",
    right: 0,
    zIndex: 1,
    boxShadow: "-3px 0 4px rgba(27,42,74,0.08)",
  },
  tablaCard: {
    background: "#fff",
    border: "1px solid #e2e6ea",
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
  },
  listaArticulos: {
    margin: 0,
    paddingLeft: "1.1rem",
  },
  th: {
    padding: "0.6rem 0.5rem",
    background: "var(--tema-primario, #1f4e96)",
    border: "1px solid #ffffff",
    fontSize: "0.72rem",
    fontWeight: 700,
    color: "#ffffff",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  td: { padding: "0.5rem 0.5rem", border: "1px solid var(--tema-linea, #d5deec)" },
  // Barrita dorada bajo los títulos de sección (igual que en las actas).
  // Botones de una solicitud recibida (cada uno abre su sección).
  barraBotonesFinal: { display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap", margin: "0.6rem 0 1.1rem" },
  botonPanelFinal: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem",
    background: "#fff",
    color: "var(--tema-primario, #1f4e96)",
    border: "1px solid #b9c7df",
    borderRadius: 10,
    padding: "0.6rem 1rem",
    fontSize: "0.86rem",
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
  },
  botonPanelFinalActivo: { background: "var(--tema-primario, #1f4e96)", color: "#ffffff", borderColor: "var(--tema-primario, #1f4e96)" },
  botonPanelFinalCuenta: { background: "rgba(31,78,150,0.12)", borderRadius: 999, padding: "0.05rem 0.55rem", fontSize: "0.74rem" },
  barraDorada: { width: 44, height: 3, background: "var(--tema-acento, #f2b705)", borderRadius: 2, margin: "0.35rem auto 0.2rem" },
  chipItem: {
    fontSize: "0.72rem",
    fontWeight: 700,
    color: "#0f7c8a",
    background: "#d5eef1",
    borderRadius: 999,
    padding: "0.1rem 0.5rem",
    whiteSpace: "nowrap",
  },
  formCotizacion: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.4rem",
    alignItems: "center",
    marginTop: "0.5rem",
    paddingTop: "0.75rem",
    borderTop: "1px dashed #d3d9df",
  },
  input: {
    padding: "0.4rem",
    fontSize: "0.85rem",
    border: "1px solid #d3d9df",
    borderRadius: 6,
    fontFamily: "inherit",
  },
  montoCalculado: {
    padding: "0.4rem 0.6rem",
    fontSize: "0.85rem",
    fontWeight: 700,
    color: "var(--tema-titulo, #0b3c60)",
    background: "#eef1f6",
    border: "1px solid #d3d9df",
    borderRadius: 6,
    whiteSpace: "nowrap",
  },
  inputPrecioItem: {
    padding: "0.3rem 0.5rem",
    fontSize: "0.8rem",
    border: "1px solid #d3d9df",
    borderRadius: 6,
    fontFamily: "inherit",
    maxWidth: 110,
  },
  formActivo: {
    marginTop: "0.75rem",
    paddingTop: "0.75rem",
    borderTop: "1px dashed #d3d9df",
  },
  eleccionActivo: {
    marginTop: "0.6rem",
    background: "#f7f8fa",
    border: "1px solid #eef1f3",
    borderRadius: 8,
    padding: "0.6rem 0.7rem",
  },
  tarjetaUnidadActivo: {
    background: "#f7f8fa",
    border: "1px solid #eef1f3",
    borderRadius: 8,
    padding: "0.6rem",
    marginTop: "0.5rem",
  },
  itemGridCustodio: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "0.5rem",
    marginTop: "0.5rem",
  },
  formEditarItem: {
    background: "#f7f8fa",
    border: "1px dashed #d3d9df",
    borderRadius: 8,
    padding: "0.75rem",
    marginBottom: "0.75rem",
  },
  gridEditarItem: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr 1fr",
    gap: "0.5rem",
  },
};

export default DetalleSolicitud;
